/**
 * Pure view model of the applications register (task T.C3, И3/И4).
 *
 * Framework-free on purpose — the `unit` Playwright project runs in plain Node,
 * where neither React nor `import.meta.env` exists (that is why the status list is
 * declared literally here instead of imported from the api layer, exactly like the
 * dashboard's `status.ts` does).
 *
 * The api owns filtering and sorting (contract #3); this module only translates
 * between the URL, the api query and the labels the UI draws. Every default is
 * OMITTED from the URL and from the request, so a default state never leaks a
 * parameter — the same rule the dashboard's `drillDownSearch` follows.
 */

// ADDITIVE (v1.8.8, T.Z43) — the audience vocabulary and its fold, shared with the api
// (a value import: the unit project resolves the built `dist`, like `types.ts` does).
// ADDITIVE (v1.9.36, T.Z143) — the VIRTUAL «Провалил ЕВИ» status token and its fold,
// the same one constant the api's filter grammar and the engine's journal event speak.
import {
  AUDIENCE_FILTERS,
  EVI_DEADLINE_DAYS,
  EVI_FAILED_STATUS_TOKEN,
  EVI_NOT_PASSED_LABEL,
  EVI_NOT_PASSED_STATUS_TOKEN,
  audienceBucketOf,
  audienceFilterOf,
  displayStatusOf,
  eviFailedSourceOf,
  // T.Z218 — the УНТИ exam level's RU label fold, shared with the api's export column.
  untiDifficultyLabel,
} from '@cb-manager/shared';

// ADDITIVE (v1.8.9, T.Z56) — the «Отдел» bucket vocabulary and its URL fold. Framework-free
// like this module, so the plain-Node unit project keeps running.
import { parseDepartmentBucket, type DepartmentBucket } from '../../departments';

import type {
  ApplicationDto,
  ApplicationDetailDto,
  ApplicationEventType,
  ApplicationListQuery,
  ApplicationStatus,
  CourseFormat,
  EventActorType,
  EventSource,
  VenueMatch,
} from '../../api/applications';

// `dashboard/format` is dependency-free (same rule this module follows), so importing
// it here keeps the model runnable in plain Node for the unit project.
import { MISSING, formatDateTime } from '../dashboard/format';
// T.Z16 — the venue-directory rows folded into combobox options (#6): the vocabulary
// the filter's «Без площадки» list and the drawer's venue combobox share. Dependency
// free, so the plain-Node unit project can still run this model.
import { venueComboboxOptions } from '../quotas/quotas-model';

// Type-only, like `manager-statuses-model.ts` does it: the combobox primitive is
// framework-free, but the import stays erased so this file never drags a component in.
import type { ComboboxOption } from '../../components/combobox-model';

// ADDITIVE (v1.8.0, Р9/#3, T.Y10) — the «Статус менеджера» filter lives in its own
// model file; the register only translates between it and the URL here.
import {
  MANAGER_STATUS_ID_PARAM,
  MANAGER_STATUS_PARAM,
  parseManagerStatusFilter,
  type ManagerStatusFilter,
} from './manager-statuses-model';

// ---------------------------------------------------------------------------
// URL state
// ---------------------------------------------------------------------------

/**
 * Query params this page owns (`lib/query-params` merges them into the URL).
 *
 * `organic` is deliberately ABSENT since A4/T.Z19 (2026-09-13): the owner did not
 * recognise the «Флаги» column it belonged to, so the column and the «Органик»
 * tri-state that filtered it came off the register together. The param survives in
 * the frozen #3, so a stale shared link that still carries `?organic=…` is inert —
 * nothing reads it, nothing sends it, and the api keeps honouring it for the export.
 */
export const PARAM = {
  STATUS: 'status',
  VENUE_ID: 'venueId',
  COURSE_ID: 'courseId',
  /** ADDITIVE (v1.8.8, T.Z43) — «Программа»: a `programs.short_name` value. */
  PROGRAM_SHORT: 'programShort',
  /** ADDITIVE (v1.8.8, T.Z43) — «Аудитория»: `8-9` / `10-11` / `СПО`. */
  AUDIENCE: 'audience',
  /**
   * ADDITIVE (v1.8.9, T.Z56) — «Отдел»: `OP|OS|NONE|MIXED`. SERVER-SIDE on this page
   * (#3 resolves it into the venue set, rows AND pagination total alike) — unlike the
   * venues/dashboard client-side narrowing, because the register is paged and its
   * total is the api's.
   */
  DEPARTMENT: 'department',
  REGION: 'region',
  FORMAT: 'format',
  /**
   * ADDITIVE (v1.9.21, T.Z101) — «Договор»: the contract-fact bucket
   * `signed | not_signed | not_arrived`. SERVER-SIDE like `department`: the register
   * is paged and its total is the api's.
   */
  CONTRACT: 'contract',
  /**
   * ADDITIVE (v1.9.49, T.Z169) — «Согласие родителей»: `true` / `false` (the api's
   * own lowercase literals). SERVER-SIDE like `contract`: the register is paged and
   * its total is the api's.
   */
  LEGAL_REP_CONSENT: 'legalRepConsent',
  /**
   * ADDITIVE (v1.9.51, T.Z172) — «Договор заключен ПП?»: `true` / `false` (the api's
   * own lowercase literals). SERVER-SIDE like `contract`: the register is paged and
   * its total is the api's.
   */
  SIGNER_MATCHES: 'signerMatches',
  /**
   * ADDITIVE (v1.9.52, T.Z176) — «Зачислен»: `true` / `false` (the api's own
   * lowercase literals) — the cat.2035 mirror's word, the dashboard card drills in
   * through this key. SERVER-SIDE like `contract`: the register is paged and its
   * total is the api's.
   */
  ENROLLED: 'enrolled',
  /**
   * ADDITIVE (v1.10.0, T.Z249 / TEC-1415) — «Распределён»: `true` / `false` (the
   * api's own lowercase literals) — the platform mirror's class fact with our live
   * study groups as the fallback. SERVER-SIDE like `contract`: the register is
   * paged and its total is the api's.
   */
  DISTRIBUTED: 'distributed',
  /**
   * ADDITIVE (v1.9.25, T.Z113) — «Провалил ЕВИ»: the auto fact the worker's registry
   * engine writes (`true` / `false`; absent = no filter). SERVER-SIDE like
   * `contract`: the register is paged and its total is the api's, and the dashboard
   * widget drills in through this key.
   */
  EVI_FAILED: 'eviFailed',
  /**
   * ADDITIVE (v1.9.48, T.Z168) — «Возраст»: the three-bucket filter
   * `under14 | 14to18 | over18`. SERVER-SIDE like `contract` (the register is paged
   * and its total is the api's).
   */
  AGE_GROUP: 'ageGroup',
  /**
   * ADDITIVE (v1.9.56, T.Z186) — «Рассылка»: a Список рассылок id (#34). SERVER-SIDE
   * like `contract`: the api narrows rows AND the pagination total to the list's
   * CURRENT recipients (an ACTIVE snapshot row), which is the owner's control over
   * what a send changed.
   */
  MAILING_LIST: 'mailingList',
  /**
   * ADDITIVE (v1.9.56, T.Z186) — «FTP»: `true` = «есть» (the УНТИ key the FTP sync
   * filled), `false` = «нет». SERVER-SIDE like `contract`: the register is paged and
   * its total is the api's.
   */
  FTP: 'ftp',
  /**
   * ADDITIVE (v1.9.66, T.Z203) — «УНТИ API»: one of the four linkage buckets
   * `found | mismatch | unti_not_found | no_unti` (the api's own lowercase
   * literals). SERVER-SIDE like `contract`: the register is paged and its total is
   * the api's — and the api resolves the bucket against the УНТИ mirror in two
   * phases, so the select only writes the URL key.
   */
  UNTI_API: 'untiApi',
  /**
   * ADDITIVE (v1.9.87, T.Z237) — «Трансферт»: the literal `none` or one of the
   * #3.1 facet's values, verbatim. SERVER-SIDE like `contract`: the register is
   * paged and its total is the api's — and the api resolves the bucket against the
   * УНТИ mirror in two phases, so the select only writes the URL key.
   */
  TRANSFERT: 'transfert',
  Q: 'q',
  HAS_VENUE: 'hasVenue',
  // ADDITIVE (v1.8.0, Р9/#3, T.Y10) — «Статус менеджера»: `managerStatus=none` is the
  // «без статуса» bucket, `managerStatusId` a dictionary id. The pair travels one at a
  // time (`manager-statuses-model.ts` owns the folding).
  MANAGER_STATUS: MANAGER_STATUS_PARAM,
  MANAGER_STATUS_ID: MANAGER_STATUS_ID_PARAM,
  /**
   * The frozen v1.1 `sort` param. The «Сортировка» selectbox that wrote it went away in
   * T.Z59; the key is read only as a legacy fallback (a bookmarked link keeps working)
   * and is never written any more.
   */
  SORT: 'sort',
  /** ADDITIVE (v1.9.3, T.Z59) — the header-click pair (`sortBy` + `order`). */
  SORT_BY: 'sortBy',
  ORDER: 'order',
  PAGE: 'page',
  PAGE_SIZE: 'pageSize',
  /** Profile drawer: `/applications?pguId=…` (the `:pguId` route is its own mount). */
  PGU_ID: 'pguId',
} as const;

/** The six buckets of docs/05 §4.1, in the contract's serialisation order. */
export const APPLICATION_STATUSES: ReadonlyArray<ApplicationStatus> = [
  'WAITING_LINK',
  'LINK_RECEIVED',
  'EVI_PASSED',
  'REJECTED',
  'AWAITING_INFO',
  'NO_DATA',
];

/** RU labels of the six statuses — the dashboard's map, reused verbatim (T.R1). */
import { STATUS_LABEL, statusTone, type BadgeTone } from '../dashboard/status';

export { STATUS_LABEL };

/**
 * ADDITIVE (v1.9.36, T.Z143) — the filter/display status vocabulary of the register:
 * the six stored statuses plus the VIRTUAL «Провалил ЕВИ» (the shared
 * `EVI_FAILED_STATUS_TOKEN`). On the wire `?status=` takes exactly these seven values
 * (#3/#18); a stored `applications.status` never carries the token.
 * ADDITIVE (v1.9.66, T.Z204) — the SECOND virtual token joins: «Не прошёл ЕВИ»
 * (`EVI_NOT_PASSED_STATUS_TOKEN`), the deadline fold — «Получил ссылку» whose ЕВИ
 * deadline (link + 10 days) is in the past with no pass. Eight values on the wire now;
 * still nothing stored.
 */
export type StatusFilterValue = ApplicationStatus | typeof EVI_FAILED_STATUS_TOKEN | typeof EVI_NOT_PASSED_STATUS_TOKEN;

export const STATUS_FILTER_VALUES: ReadonlyArray<StatusFilterValue> = [
  ...APPLICATION_STATUSES,
  EVI_FAILED_STATUS_TOKEN,
  EVI_NOT_PASSED_STATUS_TOKEN,
];

/** RU labels of the filter vocabulary — the six + the two virtual statuses, one map. */
export const STATUS_FILTER_LABEL: Readonly<Record<StatusFilterValue, string>> = {
  ...STATUS_LABEL,
  [EVI_FAILED_STATUS_TOKEN]: 'Провалил ЕВИ',
  [EVI_NOT_PASSED_STATUS_TOKEN]: EVI_NOT_PASSED_LABEL,
};

/**
 * ADDITIVE (v1.9.64, T.Z195) — the `title` the «Провалил ЕВИ» fact chip carries,
 * hoisted out of the register's page component so the cabinet's «Не перешёл» column
 * (T.Z195 — the curators' own word for the same fact) prints the SAME explanation
 * instead of a drifting copy.
 */
export const EVI_FAILED_CHIP_TITLE =
  'Провалил ЕВИ — исчез из свежего файла ЕВИ без прохода (снимается автоматически, если ребёнок вернулся)';

/** How a `venueMatchStatus` reads to a manager. */
export const VENUE_MATCH_LABEL: Readonly<Record<VenueMatch, string>> = {
  UNMATCHED: 'Не сопоставлено',
  AUTO: 'Сопоставлено автоматически',
  MANUAL: 'Назначено вручную',
};

// --- the register's column set ---------------------------------------------

/**
 * The headers of the register table, in table order — ONE list that both the
 * real table and the loading skeleton print, so the two can never disagree
 * about how wide the table is.
 *
 * Every `id` is also a `?sortBy=` value of #3 (ADDITIVE v1.9.3, T.Z59): the column
 * set IS the sort vocabulary, which is what makes a header click possible for every
 * column the table renders — with no `sortable: false` exception left since v1.9.50
 * (T.Z170 made the three derived titles sortable; before that T.Z101's «Договор»,
 * T.Z165's «Дедлайн договора» and T.Z168's «Возраст» were plain titles, because the
 * api's vocabulary had not grown with them and a control that 400s is worse than a
 * title). The derived cells map
 * onto the underlying value the
 * api sorts on: `eviDeadline` → `link_received_at` (+10 дней is a constant shift),
 * `venue` → the joined venue name, and (ADDITIVE v1.9.15, T.Z86/T.Z88) the two
 * «дней» cells → `status_changed_at` / `evi_passed_at` — the api computes the days,
 * the table only prints them. The three T.Z170 sorts are derived the same way:
 * `age` → `birth_date` INVERTED (ascending arrow = youngest first; NULLS LAST both
 * ways), `contract` → the documented rank SIGNED → NOT_SIGNED → NOT_ARRIVED → the
 * no-fact rows last (`desc` reads it forward), and `contractDeadline` — AMENDED
 * v1.9.63/T.Z191 — → the TRUE deadline instant the cell carries (the УНТИ mirror's
 * `untill_dt`, fallback evi+21): the api builds this one order in two phases around
 * the typed query and the clockless rows sink LAST both ways, so the column's order
 * is its own cells' order, not a pass-instant proxy.
 *
 * A4/T.Z19 (owner, 2026-09-13): «Флаги» came OFF this list. The owner did not
 * recognise the column, and the three states it showed (органик / отказ /
 * ручная площадка) read as noise in a register a manager scans by name, venue
 * and status. The DATA is untouched: `isOrganic` / `agreementRejected` stay on
 * the contract and on the profile card (its badges and the MANAGER toggles),
 * `venueMatchStatus` stays the «Сопоставление» row, and the xlsx/csv export
 * (#18) still carries its own «Флаги» column — only the register column went.
 *
 * T.Z86/T.Z88 (sync 16.09, v1.9.15): «Дней в статусе» sits NEXT to «Статус» (it is
 * that badge's age — the stuck child, «ждёт доп. данных» included), «Дней с ЕВИ»
 * next to «Дедлайн ЕВИ» (the other ЕВИ clock the manager reads). Values come
 * computed from the api (`daysInStatus` / `daysSinceEvi`), «N дн.» or «—»; there is
 * NO filter for either column — the owner asked for two sortable columns, and the
 * «>7 дней» colouring belongs to the T.Z87 договор work, not here.
 */
export const REGISTER_HEADERS: ReadonlyArray<RegisterColumn> = Object.freeze([
  { id: 'pguId', label: 'ЕПГУ ID' },
  { id: 'fullName', label: 'ФИО' },
  { id: 'phone', label: 'Телефон' },
  { id: 'email', label: 'Email' },
  { id: 'status', label: 'Статус' },
  { id: 'daysInStatus', label: 'Дней в статусе', num: true },
  { id: 'venue', label: 'Площадка' },
  /**
   * ADDITIVE (v1.9.94, T.Z247) — the DISTRIBUTION block, right after «Площадка»: the
   * админка's «Школа» / «Класс/Группа» / «Педагог» (Linear TEC-1337, owner 23.10:
   * «подтянуть из админки новые колонки… Очень важно понять кто распределен, а кто
   * еще нет»). One source for all three — the child's LIVE study-group link (≤1 per
   * row): the group's venue, the group's own title and its teacher; «—» on all
   * three = the child is in no live group («не распределён»). «Школа» is the GROUP's
   * venue, deliberately a different fact from the «Площадка» cell above (the
   * matcher/file attribution) — a row can read «Площадка: X» and «Школа: —» at
   * once, and that row is exactly the «assigned but not distributed» bucket.
   * `sortable: false` — the api carries no `?sortBy=` for the block (the
   * `difficultyLevel` rule: a control that 400s is worse than a title).
   */
  { id: 'groupVenueName', label: 'Школа', sortable: false },
  { id: 'groupName', label: 'Класс/Группа', sortable: false },
  { id: 'groupTeacherName', label: 'Педагог', sortable: false },
  { id: 'courseId', label: 'Курс', num: true },
  { id: 'submittedAt', label: 'Дата подачи' },
  { id: 'classCourse', label: 'Класс/курс', num: true },
  { id: 'format', label: 'Формат' },
  { id: 'eviDeadline', label: 'Дедлайн ЕВИ' },
  { id: 'daysSinceEvi', label: 'Дней с ЕВИ', num: true },
  /**
   * ADDITIVE (v1.9.48, T.Z168) — «Возраст», between the ЕВИ clock pair and «Договор»:
   * the derived whole-year age the api answers (`age`, «—» when the feed has not
   * touched the row yet). ADDITIVE (v1.9.50, T.Z170) — SORTABLE now: the api maps
   * `?sortBy=age` onto the inverted `birth_date` (ascending arrow = youngest first;
   * the undated rows sink both ways), so the header is a control like any other.
   */
  { id: 'age', label: 'Возраст', num: true },
  /**
   * ADDITIVE (v1.9.21, T.Z101) — «Договор», NEXT to «Дней с ЕВИ»: the two cells a
   * manager reads together when deciding whom to call (passed long ago + no contract
   * = the обзвон list). ADDITIVE (v1.9.50, T.Z170) — SORTABLE now: the api sorts the
   * documented fold rank SIGNED → NOT_SIGNED → NOT_ARRIVED → the no-fact rows last
   * (`desc` = the working priority, the queue order the manager reads downward).
   */
  { id: 'contract', label: 'Договор' },
  /**
   * ADDITIVE (v1.9.46, T.Z165; amended v1.9.59, T.Z187) — «Дедлайн договора», right
   * after «Договор»: the fact, then its clock. The api derives it off the УНТИ
   * auto-decline date (`unti_applications.untill_dt` of the row's leaf — fallback
   * ЕВИ + 21 календарных дней) and answers the count itself (`contractDeadlineAt` +
   * `contractDaysLeft`) — the table only formats. ADDITIVE (v1.9.50, T.Z170) —
   * SORTABLE; AMENDED v1.9.63 (T.Z191): the api sorts the TRUE deadline instant the
   * cell prints (a two-phase order — the mirror join is BY-VALUE), the rows with no
   * RUNNING clock sinking LAST both directions.
   */
  { id: 'contractDeadline', label: 'Дедлайн договора' },
  /**
   * ADDITIVE (v1.9.77, T.Z218) — the УНТИ ENTRY EXAM block, after the contract pair
   * and before «Обновлено»: the level, the three grades and their sum — the owner's
   * own ask (30.09: «add to the applications page columns with this estimations …
   * and sum of their values, to be able to sort by them»). The values come computed
   * from the api (the leaf's mirror row, the same winner the «Дедлайн договора»
   * clock reads); the cells only format. ADDITIVE (v1.9.78, T.Z219) — the owner then
   * asked to sort by EACH column, and the api grew the three grade literals to
   * match: the three grade headers are sort controls now (the same two-phase
   * mechanics the sum's had, nulls last both ways). The level stays a plain title —
   * their word, not a number, and no natural order was asked for.
   */
  { id: 'difficultyLevel', label: 'Уровень сложности (УНТИ)', sortable: false },
  { id: 'algorithmGrade', label: 'Алгоритм — оценка', num: true },
  { id: 'languageGrade', label: 'Язык — оценка', num: true },
  { id: 'motivationGrade', label: 'Мотивация — оценка', num: true },
  { id: 'gradeSum', label: 'Сумма оценок', num: true },
  /**
   * ADDITIVE (v1.9.87, T.Z237) — «Трансферт», closing the УНТИ block before
   * «Обновлено»: the cat.2035 booking word of the row's leaf-winner
   * (`transfertStatus`), THEIR vocabulary verbatim — never translated, never
   * re-cased, «—» where the mirror has no answer. `sortable: false`: the owner
   * asked the column and the filter, no order (a control that 400s is worse than
   * a title — the `difficultyLevel` escape hatch).
   */
  { id: 'transfertStatus', label: 'Трансферт', sortable: false },
  { id: 'updatedAt', label: 'Обновлено' },
]);

/** One header cell of the register table. */
export interface RegisterColumn {
  /** Stable React key — the model id, not the RU text. */
  id: string;
  /** The header the table prints (RU). */
  label: string;
  /** Right-aligned numeric column. */
  num?: boolean;
  /**
   * ADDITIVE (v1.9.21, T.Z101) — `false` = a plain title header, not a sort control.
   * Default `true`: every column the api's `?sortBy=` knows stays clickable.
   */
  sortable?: boolean;
}

// --- sort (ADDITIVE v1.9.3, T.Z59 — the api's `sortBy` + `order` pair) -------

/**
 * The `?sortBy=` vocabulary of #3 — ONE value per column the register renders, in
 * `REGISTER_HEADERS` order. A header click puts the value and its `?order=` into the
 * URL, and the api sorts the WHOLE filtered set before cutting the page.
 * ADDITIVE (v1.9.50, T.Z170) — the three derived columns joined, so the vocabulary is
 * ONE value per header again: `age`, `contract`, `contractDeadline` (each sorts on
 * the value its cell derives from — see {@link REGISTER_HEADERS}).
 * ADDITIVE (v1.9.78, T.Z219) — the three УНТИ grade columns joined: the api orders
 * each by its own parsed number with the `gradeSum` mechanics (the two-phase build
 * around the typed query, nulls last both ways, ties by id); the level column stays
 * out (a word, not a number).
 */
export const SORT_BY_FIELDS = [
  'pguId',
  'fullName',
  'phone',
  'email',
  'status',
  'daysInStatus',
  'venue',
  'courseId',
  'submittedAt',
  'classCourse',
  'format',
  'eviDeadline',
  'daysSinceEvi',
  'age',
  'contract',
  'contractDeadline',
  // ADDITIVE (v1.9.77, T.Z218) — the УНТИ exam block's sorts, in table order.
  // ADDITIVE (v1.9.78, T.Z219) — each grade sorts on its own number now (the api's
  // two-phase build; nulls last both ways, ties by id); only the level stays out.
  'algorithmGrade',
  'languageGrade',
  'motivationGrade',
  'gradeSum',
  'updatedAt',
] as const;
export type SortField = (typeof SORT_BY_FIELDS)[number];
export type SortOrder = 'asc' | 'desc';

/**
 * ADDITIVE (v1.9.77, T.Z218; widened v1.9.78, T.Z219) — the `?sortBy=` values #18
 * (the export) honours: the УНТИ exam's sum and, since v1.9.78, its three
 * individual grades — byte-identical with the api's own `EXPORT_SORT_FIELDS`
 * (`apps/api/src/exports/query.ts`). A register sorted by one of these exports IN
 * THAT ORDER; any other column's sort is dropped before the request (the
 * honest-drop rule — a file that quietly ignored the on-screen order would lie).
 */
export const EXPORT_SORT_FIELDS = ['gradeSum', 'algorithmGrade', 'languageGrade', 'motivationGrade'] as const;

export interface SortState {
  field: SortField;
  order: SortOrder;
}

/** The contract default (`updatedAt` desc) — omitted from the URL and the request. */
export const DEFAULT_SORT: SortState = { field: 'updatedAt', order: 'desc' };

/**
 * The frozen v1.1 `sort=<field>[.<dir>]` vocabulary — the selectbox's old param. The
 * selectbox is gone, but a BOOKMARKED link that still carries `?sort=fullName.desc`
 * keeps its meaning: the model reads it as a fallback while `sortBy` is absent, and
 * the next header click rewrites the state under the new keys.
 */
const LEGACY_SORT_FIELDS = ['updatedAt', 'status', 'pguId', 'fullName', 'submittedAt'] as const;

/** `true` when the state is the contract default (so no key travels at all). */
export function isDefaultSort(sort: SortState): boolean {
  return sort.field === DEFAULT_SORT.field && sort.order === DEFAULT_SORT.order;
}

/**
 * URL → sort state. Junk folds to the default (a hand-edited URL never reaches the api
 * as a request it would 400), a legacy `sort=` keeps its meaning while `sortBy` is
 * absent, and a junk `order=` next to a legal `sortBy` folds to `asc` (the api's own
 * default).
 */
export function parseSortState(params: Record<string, string>): SortState {
  const sortBy = params[PARAM.SORT_BY]?.trim();
  if (sortBy !== undefined && sortBy !== '') {
    if (!SORT_BY_FIELDS.includes(sortBy as SortField)) return DEFAULT_SORT;
    const order = params[PARAM.ORDER]?.trim();
    return { field: sortBy as SortField, order: order === 'desc' ? 'desc' : 'asc' };
  }
  // Legacy fallback — `sort=fullName.desc` (v1.1 spelling) reads as the same slice.
  const legacy = params[PARAM.SORT]?.trim();
  if (legacy !== undefined && legacy !== '') {
    const [rawField, rawDir] = legacy.split('.');
    if (LEGACY_SORT_FIELDS.includes(rawField as (typeof LEGACY_SORT_FIELDS)[number])) {
      // `updatedAt` defaulted to desc in the v1.1 grammar, every other field to asc.
      const order: SortOrder =
        rawDir === 'desc' || rawDir === 'asc' ? (rawDir as SortOrder) : rawField === 'updatedAt' ? 'desc' : 'asc';
      return { field: rawField as SortField, order };
    }
  }
  return DEFAULT_SORT;
}

/** The wire form; the contract default comes back `undefined` so it never travels. */
export function formatSortState(
  sort: SortState,
): { sortBy: SortField; order: SortOrder } | undefined {
  if (isDefaultSort(sort)) return undefined;
  return { sortBy: sort.field, order: sort.order };
}

/**
 * One header click. A column that is not sorting yet starts ASCENDING (the api's own
 * default), the active column flips — including `updatedAt`, whose default state is
 * `desc`, so the first click on it turns the default into an explicit `asc`.
 */
export function toggleSort(current: SortState, field: SortField): SortState {
  if (current.field === field) {
    return { field, order: current.order === 'asc' ? 'desc' : 'asc' };
  }
  return { field, order: 'asc' };
}

/** The arrow the header prints: the active column's direction, `null` elsewhere. */
export function sortArrow(column: SortField, sort: SortState): SortOrder | null {
  return sort.field === column ? sort.order : null;
}

/**
 * ADDITIVE (v1.9.50, T.Z170) — why the «Возраст» arrow needs no special case here:
 * the api INVERTS the direction under the hood (`?sortBy=age&order=asc` orders by
 * `birth_date` DESC — ascending age = descending birth date), so the arrow this
 * layer prints already reads naturally — up = the youngest first, down = the oldest.
 * The URL and the request carry the same `asc`/`desc` the manager sees; the flip is
 * the api's private business, documented on its own `?sortBy=` (contract v1.9.50).
 */

// --- pagination -------------------------------------------------------------

export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 200;
export const PAGE_SIZE_OPTIONS: ReadonlyArray<number> = [10, 25, 50, 100];

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

export interface ApplicationFilters {
  /**
   * ONE status or — ADDITIVE (v1.9.33, T.Z141) — the LIST a «Распределение по ЦА» cell
   * drill-down presets (`?status=LINK_RECEIVED,EVI_PASSED`, the two statuses a cell
   * counts). The api answers `status IN (…)`; a single value keeps its old shape (a
   * one-element list). ADDITIVE (v1.9.36, T.Z143) — the vocabulary is the filter's
   * seven values: the six stored statuses plus the VIRTUAL «Провалил ЕВИ»
   * ({@link StatusFilterValue}).
   */
  status?: StatusFilterValue[];
  venueId?: number;
  courseId?: number;
  /** ADDITIVE (v1.8.8, T.Z43) — «Программа»: the short caption the api resolves to ids. */
  programShort?: string;
  /** ADDITIVE (v1.8.8, T.Z43) — «Аудитория»: one of {@link AUDIENCE_FILTER_OPTIONS}. */
  audience?: string;
  /** ADDITIVE (v1.8.9, T.Z56) — «Отдел»: the venue's manager-department bucket. */
  department?: DepartmentBucket;
  region?: string;
  /** Additive (T.F1): the ЕПГУ «Формат» column, canonical. ADDITIVE (v1.9.19, T.Z99):
   *  the literal `'none'` is the «Формат не указан» bucket (`format IS NULL`). */
  format?: CourseFormat | typeof FORMAT_FILTER_NONE;
  /** ADDITIVE (v1.9.21, T.Z101) — «Договор»: the contract-fact bucket. */
  contract?: ContractFilter;
  /**
   * ADDITIVE (v1.9.49, T.Z169) — «Согласие родителей»: `true` = «Отмечено»,
   * `false` = «Не отмечено», absent = no filter. A row the sync has no answer for
   * (`legalRepConsent: null`) matches NEITHER value — «нет данных» is never a «no».
   */
  legalRepConsent?: boolean;
  /**
   * ADDITIVE (v1.9.51, T.Z172) — «Договор заключен ПП?»: `true` = «Да» (the platform
   * signer is our child), `false` = «Нет» (somebody else signed), absent = no filter.
   * A row the sync has no verdict for (`signerMatches: null` — no signer, no our
   * name, or the contract unsigned) matches NEITHER value — «—» is never a «no».
   */
  signerMatches?: boolean;
  /**
   * ADDITIVE (v1.9.52, T.Z176) — «Зачислен»: `true` = «Да» (their cat.2035 word is
   * `accepted`), `false` = «Нет» (the mirror spoke and named other literals), absent
   * = no filter. A row the sweep has not covered (`enrolled: null`) matches NEITHER
   * value — «—» is never a «no». NOT the T.Z111 enrollment pair (that one reads the
   * iSmart platform's own word).
   */
  enrolled?: boolean;
  /**
   * ADDITIVE (v1.10.0, T.Z249 / TEC-1415) — «Распределён»: `true` = «Да» (the
   * platform names a live class OR the child sits in one of our live study
   * groups), `false` = «Нет» (the mirror spoke and names no class, and no our
   * group), absent = no filter. A row with NO signal from either side (`null` —
   * no mirror row, no our group) matches NEITHER value — «—» is never a «no».
   */
  distributed?: boolean;
  /** ADDITIVE (v1.9.25, T.Z113) — «Провалил ЕВИ»: the auto fact, `true`/`false`. */
  eviFailed?: boolean;
  /** ADDITIVE (v1.9.48, T.Z168) — «Возраст»: one of {@link AGE_GROUP_FILTER_OPTIONS}. */
  ageGroup?: AgeGroupFilter;
  /**
   * ADDITIVE (v1.9.56, T.Z186) — «Рассылка»: the Список рассылок id (#34) whose CURRENT
   * recipients the register shows. The options come from the #34.1 index the mailings
   * page reads (the same client), the value travels as `?mailingList=`.
   */
  mailingList?: number;
  /**
   * ADDITIVE (v1.9.56, T.Z186) — «FTP»: `true` = «есть», `false` = «нет`, over the
   * УНТИ key the FTP sync fills; absent = no filter.
   */
  ftp?: boolean;
  /**
   * ADDITIVE (v1.9.66, T.Z203) — «УНТИ API»: one of {@link UNTI_API_FILTER_OPTIONS}'s
   * four buckets, the row's linkage to the УНТИ mirror (`found` = the mirror's latest
   * row for the row's leaf carries the row's own `pgu_id`; `mismatch` = it names a
   * different one; `unti_not_found` = the leaf is absent from the mirror; `no_unti` =
   * no `unti_id` on the row). Absent = no filter.
   */
  untiApi?: UntiApiLink;
  /**
   * ADDITIVE (v1.9.87, T.Z237) — «Трансферт»: the literal `none` or one of the
   * values the #3.1 facet offers (their own words, verbatim), absent = no filter.
   * A hand-edited value rides verbatim (the `programShort` reading — the api 400s
   * what no winner row carries); the select renders it selected until the manager
   * picks another.
   */
  transfert?: string;
  q?: string;
  hasVenue?: boolean;
  /** ADDITIVE (v1.8.0, Р9/#3, T.Y10) — `managerStatusId=N` or the «без статуса» bucket. */
  managerStatus?: ManagerStatusFilter;
  page: number;
  pageSize: number;
  /** ADDITIVE (v1.9.3, T.Z59) — the header-click sort state (`sortBy` + `order`). */
  sort: SortState;
}

export const DEFAULT_FILTERS: ApplicationFilters = { page: 1, pageSize: DEFAULT_PAGE_SIZE, sort: DEFAULT_SORT };

/** Positive integer or `undefined` (a junk value is a URL typo, not an api error). */
export function parseIntParam(value: string | undefined): number | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** `hasVenue=true|false`; anything else means "don't care". */
export function parseBoolParam(value: string | undefined): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

/**
 * URL → status filter. ADDITIVE (v1.9.33, T.Z141) — reads ONE value or a comma-separated
 * LIST (`LINK_RECEIVED,EVI_PASSED`, what the ЦА cell drill-down sends); the single value
 * keeps its exact pre-v1.9.33 answer (a one-element list). Tokens are trimmed and folded
 * to the enum's UPPER_SNAKE spelling, duplicates collapse, and a token the api would 400
 * is DROPPED rather than sent — the same «never hands the api a 400» rule every junk
 * filter here follows (a hand-edited link narrows to the statuses it named, never to an
 * error page). No valid token at all ⇒ no filter. ADDITIVE (v1.9.36, T.Z143) — the
 * VIRTUAL `EVI_FAILED` is a legal token now, exactly what the api accepts.
 */
export function parseStatusParam(value: string | undefined): StatusFilterValue[] | undefined {
  if (typeof value !== 'string') return undefined;
  const values: StatusFilterValue[] = [];
  for (const token of value.split(',')) {
    const candidate = token.trim().toUpperCase();
    if (!STATUS_FILTER_VALUES.includes(candidate as StatusFilterValue)) continue;
    if (!values.includes(candidate as StatusFilterValue)) values.push(candidate as StatusFilterValue);
  }
  return values.length > 0 ? values : undefined;
}

/**
 * The options of the «Статус» select for the URL it renders (ADDITIVE v1.9.33, T.Z141):
 * «Все статусы», the six singles — plus, when the URL carries a multi-value preset, ONE
 * injected option whose value IS the wire string, so the arrival renders as SELECTED
 * instead of reading as «Все статусы». The same «a link must never read as nothing
 * selected» injection the department / program-short / manager-status filters already
 * make for a value their list has no option of its own for; the label names the preset
 * («Ссылка получена + ЕВИ пройден»). Picking any single option replaces the preset,
 * picking «Все статусы» clears it — one control, no second paradigm.
 * ADDITIVE (v1.9.36, T.Z143) — «Провалил ЕВИ» joins the singles, the owner's own ask:
 * the virtual status is selectable like any of the six.
 */
export interface StatusFilterOption {
  value: string;
  label: string;
}

export function statusFilterOptions(rawStatus: string | undefined): StatusFilterOption[] {
  const options: StatusFilterOption[] = [{ value: '', label: 'Все статусы' }];
  for (const status of STATUS_FILTER_VALUES) {
    options.push({ value: status, label: STATUS_FILTER_LABEL[status] });
  }
  const raw = typeof rawStatus === 'string' ? rawStatus.trim() : '';
  if (raw === '' || STATUS_FILTER_VALUES.includes(raw as StatusFilterValue)) return options;
  const parsed = parseStatusParam(raw);
  const label = parsed !== undefined ? parsed.map((status) => STATUS_FILTER_LABEL[status]).join(' + ') : raw;
  options.push({ value: raw, label });
  return options;
}

/**
 * ADDITIVE (v1.9.36, T.Z143) — how the register/profile badge reads one row: the
 * VIRTUAL «Провалил ЕВИ» when either fact source holds (the shared `displayStatusOf`
 * fold — auto or the T.Z96 manual one), the stored status otherwise. Red ink (`bad` —
 * the tone REJECTED uses and the dashboard's «Провалил ЕВИ» card wears); the title
 * names the SOURCE, so the manual/auto distinction survives the fold.
 * ADDITIVE (v1.9.66, T.Z204) — the SECOND virtual value: «Не прошёл ЕВИ» on a
 * LINK_RECEIVED row whose ЕВИ deadline has lapsed with no pass — judged by `now`
 * (default: the render instant, the same clock the «Дедлайн ЕВИ» cell is read by).
 * The precedence is the shared fold's: «Провалил ЕВИ» first, then the clock, then the
 * stored status. Red ink too (`bad`) — the owner asked for a red pill: this row needs
 * a manager, exactly like «Отказ» and «Провалил ЕВИ».
 */
export interface DisplayStatusView {
  status: StatusFilterValue;
  label: string;
  tone: BadgeTone;
  title: string;
}

export function displayStatusView(
  row: {
    status: ApplicationStatus;
    eviFailed?: boolean | null;
    eviFailedManually?: boolean | null;
    linkReceivedAt?: string | null;
    eviPassedAt?: string | null;
  },
  now: Date = new Date(),
): DisplayStatusView {
  const status = displayStatusOf(row, now);
  if (status !== EVI_FAILED_STATUS_TOKEN && status !== EVI_NOT_PASSED_STATUS_TOKEN) {
    return { status, label: STATUS_LABEL[status], tone: statusTone(status), title: STATUS_LABEL[status] };
  }
  if (status === EVI_NOT_PASSED_STATUS_TOKEN) {
    return {
      status,
      label: STATUS_FILTER_LABEL[status],
      tone: 'bad',
      title: EVI_NOT_PASSED_CHIP_TITLE,
    };
  }
  const source = eviFailedSourceOf(row);
  const title =
    source === 'manual'
      ? 'Провалил ЕВИ (вручную) — проставлено менеджером'
      : 'Провалил ЕВИ — исчез из свежего файла ЕВИ без прохода (снимается автоматически, если ребёнок вернулся)';
  return { status, label: STATUS_FILTER_LABEL[status], tone: 'bad', title };
}

/**
 * ADDITIVE (v1.9.66, T.Z204) — the `title` the «Не прошёл ЕВИ» pill carries: WHY the
 * row is red, in the explainer's own words, so a tooltip answers the question the
 * badge raises. Named next to `EVI_FAILED_CHIP_TITLE` so the two virtual chips read
 * the same way.
 */
export const EVI_NOT_PASSED_CHIP_TITLE =
  'Не прошёл ЕВИ — ссылка получена, но 10 дней на прохождение истекли, а прохождения нет';

/** The `format` vocabulary #3 accepts (T.F1). #18's `appFormat` accepts EXACTLY this
 *  list — the v1.9.19 `none` literal below is the register's own bucket. */
export const FORMAT_FILTERS: ReadonlyArray<CourseFormat> = ['ONLINE', 'OFFLINE'];

/**
 * ADDITIVE (v1.9.19, T.Z99) — the «Формат не указан» bucket of #3: the literal the api
 * answers with `format IS NULL`. The 2026-09-16+ ЕПГУ exports stopped filling the raw
 * cell, so NEW rows legitimately carry no format — per the owner they stay empty, the
 * register renders «—», and this is how a manager filters for them. Lowercase on
 * purpose, the same literal-`none` precedent `managerStatus=none` and `groupId=none`
 * set.
 */
export const FORMAT_FILTER_NONE = 'none';

export const FORMAT_LABEL: Readonly<Record<CourseFormat, string>> = {
  OFFLINE: 'Офлайн',
  ONLINE: 'Онлайн',
};

/** The «Формат обучения» select of the register, in table order, `none` last. */
export const FORMAT_FILTER_OPTIONS: ReadonlyArray<{ value: CourseFormat | typeof FORMAT_FILTER_NONE; label: string }> = [
  ...FORMAT_FILTERS.map((format) => ({ value: format, label: FORMAT_LABEL[format] })),
  { value: FORMAT_FILTER_NONE, label: 'Формат не указан' },
];

/**
 * `format=online` → `ONLINE`; `format=none` → the literal; anything else means
 * "don't care" (the api would 400 it). The literal is spelled EXACTLY lowercase —
 * `NONE` is not a value, exactly as on the wire.
 */
export function parseFormatParam(value: string | undefined): CourseFormat | typeof FORMAT_FILTER_NONE | undefined {
  const candidate = typeof value === 'string' ? value.trim() : '';
  if (candidate === FORMAT_FILTER_NONE) return FORMAT_FILTER_NONE;
  return FORMAT_FILTERS.includes(candidate.toUpperCase() as CourseFormat)
    ? (candidate.toUpperCase() as CourseFormat)
    : undefined;
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.21, T.Z101) — «Договор»: the contract facts and their filter
// ---------------------------------------------------------------------------

/**
 * The contract-fact bucket vocabulary #3 accepts — the wire spells it lowercase.
 * ADDITIVE (v1.9.24, T.Z111) — the enrollment pair joins the same pattern arm:
 * five lowercase literals now.
 */
export const CONTRACT_FILTERS = ['signed', 'not_signed', 'not_arrived', 'enrolled', 'declined'] as const;
export type ContractFilter = (typeof CONTRACT_FILTERS)[number];

/** The derived `contractState` the api computes — the chip's state, not a funnel status. */
export type ContractState = NonNullable<ApplicationDto['contractState']>;

/**
 * ADDITIVE (v1.9.24, T.Z111) — the derived `enrollState` the api computes, the
 * enrollment twin of {@link ContractState}. Its own axis on purpose: the contract
 * state is about the CONTRACT DOCUMENT, this one is about the enrollment decision.
 */
export type EnrollState = NonNullable<ApplicationDto['enrollState']>;

/** The «Договор» select of the register, in the funnel's own reading order. */
export const CONTRACT_FILTER_OPTIONS: ReadonlyArray<{ value: ContractFilter; label: string }> = [
  { value: 'signed', label: 'Договор подписан' },
  { value: 'not_signed', label: 'Не подписан' },
  { value: 'not_arrived', label: 'Не пришёл' },
  // ADDITIVE (v1.9.24, T.Z111) — the two enrollment buckets the sync learned.
  { value: 'enrolled', label: 'Зачислен' },
  { value: 'declined', label: 'Отказано в зачислении' },
];

/**
 * The chip/сell text of ONE contract state, and the RU label the select prints.
 * Dateless on purpose here — the row cell and the card chip render the signing day
 * next to the label (see `contractCellText`).
 */
export const CONTRACT_STATE_LABEL: Readonly<Record<Exclude<ContractState, null>, string>> = {
  SIGNED: 'Договор подписан',
  NOT_SIGNED: 'Не подписан',
  NOT_ARRIVED: 'Не пришёл',
};

/** Shorter wording for the narrow table cell (the card keeps the full label). */
export const CONTRACT_STATE_SHORT: Readonly<Record<Exclude<ContractState, null>, string>> = {
  SIGNED: 'Подписан',
  NOT_SIGNED: 'Не подписан',
  NOT_ARRIVED: 'Не пришёл',
};

/** `contract=not_signed` → `not_signed`; anything the api would 400 never leaves the browser. */
export function parseContractParam(value: string | undefined): ContractFilter | undefined {
  if (typeof value !== 'string') return undefined;
  const candidate = value.trim();
  return CONTRACT_FILTERS.includes(candidate as ContractFilter) ? (candidate as ContractFilter) : undefined;
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.48, T.Z168) — «Возраст»: the three buckets and their filter
// ---------------------------------------------------------------------------

/**
 * The `?ageGroup=` vocabulary of #3/#18 — the wire spells it lowercase, exactly the
 * values the api's pattern arm names (`CONTRACT_FILTERS` precedent). The RU labels
 * are the owner's own wording.
 * ADDITIVE (v1.9.50, T.Z170) — the FOURTH literal `unknown` («Не заполнен»): the
 * rows whose birth date the feed does not know (`birth_date IS NULL`) — the feed may
 * simply not have the child, and «не заполнен» is the honest name for that, never a
 * guessed bucket. The three range buckets keep excluding those rows, so the four
 * values partition the register.
 * T.Z181 (v1.9.54) — the third bucket's label is «От 18» (was «Старше 18»): the
 * boundary moved, `over18` now answers age ≥ 18 and an exactly-18-year-old no longer
 * sits in «От 14 до 18» (14 ≤ age < 18). Only the LABEL changed here — the wire
 * values and the request are identical.
 */
export const AGE_GROUP_FILTERS = ['under14', '14to18', 'over18', 'unknown'] as const;
export type AgeGroupFilter = (typeof AGE_GROUP_FILTERS)[number];

/** The «Возраст» select of the register, in the owner's own reading order — «Не заполнен» LAST. */
export const AGE_GROUP_FILTER_OPTIONS: ReadonlyArray<{ value: AgeGroupFilter; label: string }> = [
  { value: 'under14', label: 'Меньше 14 лет' },
  { value: '14to18', label: 'От 14 до 18' },
  { value: 'over18', label: 'От 18' },
  { value: 'unknown', label: 'Не заполнен' },
];

/** `ageGroup=under14` → `under14`; anything the api would 400 never leaves the browser. */
export function parseAgeGroupParam(value: string | undefined): AgeGroupFilter | undefined {
  if (typeof value !== 'string') return undefined;
  const candidate = value.trim();
  return AGE_GROUP_FILTERS.includes(candidate as AgeGroupFilter) ? (candidate as AgeGroupFilter) : undefined;
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.56, T.Z186) — «Рассылка» + «FTP»: the send control and the УНТИ axis
// ---------------------------------------------------------------------------

/**
 * The two options of the «FTP» select, the api's own lowercase literals. Deliberately
 * TWO and not three: `unti_id` is either on the row or it is not, so the two values
 * partition the register — there is no «нет данных» bucket to protect, unlike the
 * tri-state sync facts («Согласие родителей» and friends keep their two-options-for-a-
 * tri-state rule for the opposite reason).
 */
export const FTP_FILTER_OPTIONS: ReadonlyArray<{ value: 'true' | 'false'; label: string }> = [
  { value: 'true', label: 'Есть' },
  { value: 'false', label: 'Нет' },
];

/** `ftp=true|false` → the boolean; a junk or hand-edited value degrades to «no filter». */
export function parseFtpParam(value: string | undefined): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.66, T.Z203) — «УНТИ API»: the row's linkage to the УНТИ mirror
// ---------------------------------------------------------------------------

/**
 * The four «УНТИ API» buckets, exactly the api's lowercase literals (CASE-SENSITIVE —
 * the `?contract=` grammar, so a hand-edited `FOUND` degrades to «no filter» here the
 * way every junk param does, and 400s at the api). The four PARTITION the register:
 *   * `found` — the mirror's LATEST row for the row's leaf carries the row's OWN
 *     `pgu_id` (any mirror status counts);
 *   * `mismatch` — that winner names a DIFFERENT `pgu_id` (the twin state);
 *   * `unti_not_found` — the leaf is absent from the mirror entirely;
 *   * `no_unti` — the row carries no `unti_id` at all.
 * Labels are the owner's own words for the buckets (25.09 brief), quoted verbatim.
 */
export type UntiApiLink = 'found' | 'mismatch' | 'unti_not_found' | 'no_unti';

export const UNTI_API_FILTER_OPTIONS: ReadonlyArray<{ value: UntiApiLink; label: string }> = [
  { value: 'found', label: 'Найден' },
  { value: 'mismatch', label: 'Не найден по epgu_id + unti_id' },
  { value: 'unti_not_found', label: 'Не найден epgu_id' },
  { value: 'no_unti', label: 'Не найден unti_id' },
];

/** `untiApi=<literal>` → the value; a junk or hand-edited value degrades to «no filter». */
export function parseUntiApiParam(value: string | undefined): UntiApiLink | undefined {
  return UNTI_API_FILTER_OPTIONS.some((option) => option.value === value) ? (value as UntiApiLink) : undefined;
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.87, T.Z237) — «Трансферт»: the УНТИ leaf-winner's booking word
// ---------------------------------------------------------------------------

/**
 * ADDITIVE (v1.9.87, T.Z237) — the «no value» literal of the `?transfert=` grammar:
 * a row with no УНТИ key, a leaf the mirror does not know, or a winner row whose
 * own word is NULL — the same bucket the register's «—» cell renders. The api's
 * own literal (`?managerStatus=none` / `?format=none` precedent), never a mirror
 * word.
 */
export const TRANSFERT_FILTER_NONE = 'none';

/** The select's label for the {@link TRANSFERT_FILTER_NONE} bucket. */
export const TRANSFERT_NONE_LABEL = 'Не указан';

/**
 * ADDITIVE (v1.9.87, T.Z237) — the «Трансферт» select's options over the #3.1
 * facet's answer ({@link useTransfertValues} — the DISTINCT winner-fold words the
 * api itself offers, so an option can never be a value the filter 400s). The list
 * is the facet's own order (alphabetical), values travel VERBATIM — their
 * vocabulary is never translated, never re-cased (`conditionally_activated` stays
 * `conditionally_activated`, the same word the cell prints). A value the URL
 * carries that the facet does not know (a hand-edited link, a word the mirror has
 * since stopped carrying) stays selectable as itself — a filter must never read
 * as «nothing selected», the venue/program-short rescue. Pure, so the unit
 * project pins the fold without a DOM.
 */
export function transfertFilterOptions(
  values: ReadonlyArray<string>,
  rawValue: string | undefined,
): Array<{ value: string; label: string }> {
  const options = values.map((value) => ({ value, label: value }));
  const raw = typeof rawValue === 'string' ? rawValue.trim() : '';
  if (raw !== '' && raw !== TRANSFERT_FILTER_NONE && !options.some((option) => option.value === raw)) {
    options.unshift({ value: raw, label: raw });
  }
  return options;
}

/**
 * ADDITIVE (v1.9.87, T.Z237) — `transfert=<value>` → the value, trimmed; a junk or
 * hand-edited value RIDES VERBATIM (the `programShort` reading — the vocabulary is
 * data, the browser cannot know it, and the api answers the unknown word with its
 * own 400 rather than a silently empty page). `none` is the api's own literal and
 * travels as-is.
 */
export function parseTransfertParam(value: string | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * The «Рассылка» select's options, folded from the #34.1 index (id + NAME — the owner
 * reads lists by their names). An id the URL carries that the index does not know (a
 * hand-typed link) stays selectable as «Рассылка N» — a filter must never read as
 * «nothing selected», the same rescue the venue/program-short/manager-status filters
 * apply. Pure, so the unit project pins the fold without a DOM.
 */
export function mailingListFilterOptions(
  lists: ReadonlyArray<{ id: number; name: string }>,
  rawValue: string | undefined,
): Array<{ value: string; label: string }> {
  const options = lists.map((list) => ({ value: String(list.id), label: list.name }));
  const raw = typeof rawValue === 'string' ? rawValue.trim() : '';
  if (raw !== '' && !options.some((option) => option.value === raw)) {
    options.unshift({ value: raw, label: `Рассылка ${raw}` });
  }
  return options;
}

/**
 * The «Возраст» cell: the whole-year age the api derived, or `null` = «—» (the feed
 * has not touched the row — no birth date, no honest age). The api owns the math (one
 * instant per page, like the two «дней» cells); this layer only formats.
 */
export function ageCellText(value: number | null): string {
  return value === null ? MISSING : String(value);
}

/**
 * ADDITIVE (v1.9.77, T.Z218) — the УНТИ exam cells: a parsed grade number the api
 * derived off the leaf's TEXT column, or `null` = «—» (the mirror holds no fact for
 * this leaf, or the value was not a plain decimal). The number prints as it is —
 * the same value the #18 cell carries, so the screen and the sheet read alike.
 */
export function gradeCellText(value: number | null): string {
  return value === null ? MISSING : String(value);
}

/**
 * ADDITIVE (v1.9.77, T.Z218) — the «Уровень сложности (УНТИ)» cell: the shared
 * `untiDifficultyLabel` fold («Базовый» / «Повышенный», an unknown literal as
 * itself — the mirror's own word is never blanked by a vocabulary we have not
 * translated), `null` = «—». The api keeps the raw word on the wire; this is the
 * only place it turns RU, the same fold the #18 column applies.
 */
export function difficultyCellText(value: string | null): string {
  const label = untiDifficultyLabel(value);
  return label === null ? MISSING : label;
}

/**
 * ADDITIVE (v1.9.87, T.Z237) — the «Трансферт» cell: the УНТИ winner's own word,
 * VERBATIM — no label fold here (their vocabulary is never translated, never
 * re-cased; the api's `difficultyLevel` rule without the RU fold), `null` = «—»
 * (no leaf, no mirror row, or the winner row says nothing). The same string the
 * #18 «Трансферт» column prints, so the screen and the sheet read alike.
 */
export function transfertCellText(value: string | null): string {
  return value === null ? MISSING : value;
}

/**
 * ADDITIVE (v1.9.49, T.Z169) — `legalRepConsent=true|false` → the boolean; a junk or
 * hand-edited value degrades to «no filter», never to a request the api would 400
 * (the same reading every boolean param here follows).
 */
export function parseLegalRepConsentParam(value: string | undefined): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

/**
 * ADDITIVE (v1.9.51, T.Z172) — `signerMatches=true|false` → the boolean; a junk or
 * hand-edited value degrades to «no filter», never to a request the api would 400
 * (the same reading every boolean param here follows).
 */
export function parseSignerMatchesParam(value: string | undefined): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

/**
 * ADDITIVE (v1.9.52, T.Z176) — `enrolled=true|false` → the boolean; a junk or
 * hand-edited value degrades to «no filter», never to a request the api would 400
 * (the same reading every boolean param here follows).
 */
export function parseEnrolledParam(value: string | undefined): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

/**
 * ADDITIVE (v1.10.0, T.Z249 / TEC-1415) — `distributed=true|false` → the boolean; a
 * junk or hand-edited value degrades to «no filter», never to a request the api
 * would 400 (the same reading every boolean param here follows).
 */
export function parseDistributedParam(value: string | undefined): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

/**
 * The table cell / card chip content: the short label plus, for a signed child, the
 * DAY of the signing («Подписан 10.09.2026») — the fact a manager chases. `null` =
 * «no fact yet», which renders «—»: the sync has not spoken about this row, and
 * printing «не подписан» would be a guess.
 */
export function contractCellText(
  application: Pick<ApplicationDto, 'contractState' | 'contractSignedAt'>,
): string | null {
  if (application.contractState === null) return null;
  if (application.contractState === 'SIGNED' && application.contractSignedAt !== null) {
    return `${CONTRACT_STATE_SHORT.SIGNED} ${formatDateOnly(application.contractSignedAt)}`;
  }
  return CONTRACT_STATE_SHORT[application.contractState];
}

/**
 * T.Z105 (owner scope extension, 2026-09-17) — the pill tone of a contract state, the
 * SAME green/red the dashboard's contract-funnel widget inks (`badge--ok` /
 * `badge--bad`, i.e. `--status-active-*` / `--status-expired-*`), so the two screens
 * read as one colour system: signed = done (green), not signed / not arrived = a queue
 * that needs a person (red). The cell renders the tone through the shared `<Badge>` —
 * the exact chrome the register's derived-status column already pills with, so radius,
 * padding and font come free. `null` (no fact yet) gets no tone at all: it stays the
 * quiet «—», never a pill that would guess at a fact the sync has not reported.
 */
export const CONTRACT_STATE_TONE: Readonly<Record<Exclude<ContractState, null>, 'ok' | 'bad'>> = {
  SIGNED: 'ok',
  NOT_SIGNED: 'bad',
  NOT_ARRIVED: 'bad',
};

/**
 * ADDITIVE (v1.9.24, T.Z111) — the enrollment fact as its own pill, beside the
 * contract pill in the same «Договор» cell: «Зачислен» green (the done bucket) and
 * «Отказано в зачислении» red (a queue that needs a person), the same `<Badge>`
 * chrome and the same ink pair the contract pill uses. A row with no enrollment fact
 * renders no enrollment pill at all — the quiet «—» logic stays per axis, so «no
 * contract news» can never read as «не зачислен».
 */
export const ENROLL_STATE_LABEL: Readonly<Record<Exclude<EnrollState, null>, string>> = {
  ENROLLED: 'Зачислен',
  DECLINED: 'Отказано в зачислении',
};

export const ENROLL_STATE_TONE: Readonly<Record<Exclude<EnrollState, null>, 'ok' | 'bad'>> = {
  ENROLLED: 'ok',
  DECLINED: 'bad',
};

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.49, T.Z169) — «Получено согласие родителей», the drawer's row in
// the Договор block and the register's filter select
// ---------------------------------------------------------------------------

/** The two options of the «Согласие родителей» select, the api's own lowercase literals. */
export const LEGAL_REP_CONSENT_FILTER_OPTIONS: ReadonlyArray<{ value: 'true' | 'false'; label: string }> = [
  { value: 'true', label: 'Отмечено' },
  { value: 'false', label: 'Не отмечено' },
];

/** What the drawer's consent row prints — the mark, the word, the ink. */
export interface LegalRepConsentView {
  /** `ok` = calm green («Отмечено»), `bad` = danger, `none` = the quiet muted row. */
  tone: 'ok' | 'bad' | 'none';
  /** `✓` / `✗`, `null` for the «—» row (no mark without a fact). */
  mark: '✓' | '✗' | null;
  /** `«Отмечено»` / `«Не отмечено»` / `«—»`. */
  label: string;
  /** The `title` the row carries — WHY the danger ink, when it is danger. */
  title: string;
}

/**
 * The drawer's consent row view (T.Z169, the owner's Владимир-Костин case): the
 * platform's checkbox as the family's answer, in three honest states.
 *
 *   * `true`  → ✓ «Отмечено», calm green — the fact is as it should be;
 *   * `false` → ✗ «Не отмечено», DANGER ink only while the contract is already
 *     signed — «договор подписан, а согласия родителей нет» is exactly the case the
 *     owner asked the screen for. A child whose contract has not arrived (or has not
 *     been signed) keeps the quiet muted wording: the missing tick is no news yet;
 *   * `null`  → «—» — the sync has no live platform row for the child, which is NEVER
 *     a «не отмечено» answer (the filter matches a `null` row never, same rule).
 *
 * Pure and a function of the row alone; the api owns the fact, this layer only names it.
 */
export function legalRepConsentView(
  application: Pick<ApplicationDto, 'legalRepConsent' | 'contractSigned'>,
): LegalRepConsentView {
  const consent = application.legalRepConsent ?? null;
  if (consent === null) {
    return { tone: 'none', mark: null, label: '—', title: 'Синк договоров ещё не отмечал это заявление' };
  }
  if (consent) {
    return { tone: 'ok', mark: '✓', label: 'Отмечено', title: 'Родители подтвердили согласие на платформе' };
  }
  return {
    tone: application.contractSigned ? 'bad' : 'none',
    mark: '✗',
    label: 'Не отмечено',
    title: application.contractSigned
      ? 'Договор подписан, но согласие родителей на платформе не отмечено'
      : 'Согласие родителей на платформе не отмечено',
  };
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.51, T.Z172) — «Договор заключен ПП?», the drawer's signer rows in
// the Договор block and the register's filter select. ПП = получатель поддержки —
// the child.
// ---------------------------------------------------------------------------

/** The two options of the «Договор заключен ПП?» select, the api's own lowercase literals. */
export const SIGNER_MATCHES_FILTER_OPTIONS: ReadonlyArray<{ value: 'true' | 'false'; label: string }> = [
  { value: 'true', label: 'Да' },
  { value: 'false', label: 'Нет' },
];

/** The two options of the «Зачислен» select, the api's own lowercase literals (T.Z176). */
export const ENROLLED_FILTER_OPTIONS: ReadonlyArray<{ value: 'true' | 'false'; label: string }> = [
  { value: 'true', label: 'Да' },
  { value: 'false', label: 'Нет' },
];

/**
 * ADDITIVE (v1.10.0, T.Z249 / TEC-1415) — the two options of the «Распределён»
 * select, the api's own lowercase literals. Deliberately TWO, not three: a row with
 * no signal from either side (no mirror row, no our live group) matches NEITHER
 * value, so «—» can never be folded into «Нет».
 */
export const DISTRIBUTED_FILTER_OPTIONS: ReadonlyArray<{ value: 'true' | 'false'; label: string }> = [
  { value: 'true', label: 'Да' },
  { value: 'false', label: 'Нет' },
];

/** What the drawer's signer row prints — the mark, the word, the ink. */
export interface SignerMatchesView {
  /** `ok` = calm green («Да»), `bad` = danger («Нет»), `none` = the quiet «—» row. */
  tone: 'ok' | 'bad' | 'none';
  /** `✓` / `✗`, `null` for the «—» row (no mark without a verdict). */
  mark: '✓' | '✗' | null;
  /** `«Да»` / `«Нет»` / `«—»`. */
  label: string;
  /** The `title` the row carries — WHY the danger ink, when it is danger. */
  title: string;
}

/**
 * The drawer's signer-verdict row view (T.Z172, the owner's CSV-audit case: 12 of
 * 2 810 signed contracts whose iSmart identity is not our child's). Three honest
 * states, exactly the ones the column stores:
 *
 *   * `true`  → ✓ «Да», calm green — the person who pressed «Подписать» in iSmart is
 *     our child (folded Фамилия Имя);
 *   * `false` → ✗ «Нет», DANGER ink — somebody else signed (the owner's alarm; the
 *     «Подписал (по данным CAT)» line under it names who);
 *   * `null`  → «—» — no verdict: no signer name, no our name, or the contract is
 *     unsigned (the verdict only exists for a signed contract), which is NEVER a
 *     «нет» answer (the filter matches a `null` row never, same rule).
 *
 * Pure and a function of the row alone; the api owns the verdict, this layer only
 * names it.
 */
export function signerMatchesView(
  application: Pick<ApplicationDto, 'signerMatches'>,
): SignerMatchesView {
  const verdict = application.signerMatches ?? null;
  if (verdict === null) {
    return { tone: 'none', mark: null, label: '—', title: 'Нет ответа: подписавший не сравнён с ребёнком' };
  }
  if (verdict) {
    return { tone: 'ok', mark: '✓', label: 'Да', title: 'Договор в iSmart подписал получатель поддержки' };
  }
  return {
    tone: 'bad',
    mark: '✗',
    label: 'Нет',
    title: 'Договор в iSmart подписал не получатель поддержки — смотрите строку «Подписал»',
  };
}

/**
 * What the drawer's «Зачислен» chip prints — the mark, the word, the ink (T.Z176).
 */
export interface EnrolledView {
  /** `ok` = calm green («Да»), `none` = the quiet rows («Нет» / «—»). */
  tone: 'ok' | 'none';
  /** `✓`, `null` for the quiet rows (a missing mark, not a «no» mark). */
  mark: '✓' | null;
  /** `«Да»` / `«Нет»` / `«—»`. */
  label: string;
  /** The `title` the chip carries — what the word means and where it comes from. */
  title: string;
}

/**
 * The drawer's «Зачислен» chip view (T.Z176 — the cat.2035.university mirror's own
 * word, the plain `status` of their `ticket_application`). Three honest states,
 * exactly the ones the column stores:
 *
 *   * `true`  → ✓ «Да», calm green — their word is `accepted` (the owner's rule:
 *     Да = ACCEPTED, case-insensitive);
 *   * `false` → «Нет», NEUTRAL ink on purpose — this is a live-mirror fact about
 *     their vocabulary (`approved` / `declined` / anything new), not an alarm like
 *     the signer mismatch; the funnel status beside it stays the authority;
 *   * `null`  → «—» — the unti2035 sweep has not covered the row (no `unti_id`, or
 *     no received page has named it), which is NEVER a «нет» answer (the
 *     `?enrolled=` filter matches a `null` row never, same rule).
 *
 * Pure and a function of the row alone; the api owns the fact, this layer only
 * names it.
 */
export function enrolledView(application: Pick<ApplicationDto, 'enrolled'>): EnrolledView {
  const fact = application.enrolled ?? null;
  if (fact === null) {
    return { tone: 'none', mark: null, label: '—', title: 'Нет данных: синхронизация УНТИ ещё не накрыла это заявление' };
  }
  if (fact) {
    return { tone: 'ok', mark: '✓', label: 'Да', title: 'Статус заявления в системе УНТИ (cat.2035) — accepted' };
  }
  return { tone: 'none', mark: null, label: 'Нет', title: 'Статус заявления в системе УНТИ (cat.2035) — не accepted' };
}

/**
 * ADDITIVE (v1.9.51, T.Z172) — the signer's identity line, rendered ONLY when the
 * sync brought a name (the name is staff-visible data, same as the rest of the
 * drawer): «Подписал (по данным CAT): <имя>». CAT = the future platform's own
 * database the worker reads. `null` input renders `null` — the line is simply absent,
 * never an empty label.
 */
export function signerNameLine(signerName: string | null | undefined): string | null {
  const name = typeof signerName === 'string' ? signerName.trim() : '';
  return name === '' ? null : `Подписал (по данным CAT): ${name}`;
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.46, T.Z165) — «Дедлайн договора», the derived contract clock
// ---------------------------------------------------------------------------

/**
 * The rule AMENDED by the owner (v1.9.59, T.Z187 — ruling 24.09, reversing the
 * v1.9.46 reading): «Дедлайн договора» IS «Дата автоотклонения» in УНТИ — the mirror's
 * `untill_dt`, because УНТИ rejects the application at that instant and that is the
 * real last moment the contract can be signed. The api OWNS the math —
 * `contractDeadlineAt` + `contractDaysLeft`, one instant per page, derived from the
 * mirror date and never stored; this layer only formats, the way `daysCellText` does
 * for the two «дней» cells (re-deriving here would put a second clock on the page that
 * can quietly disagree with the answer the api served).
 *
 * The constant below is the api's FALLBACK arithmetic — `evi_passed_at + 21
 * календарных дней` (= 15 рабочих), kept for a row the mirror says nothing to (no
 * `unti_id`, no mirror row, or a null/unparseable `untill_dt`) so the column never
 * goes blank. It travels in the two `title` tooltips as the honest «or, without УНТИ
 * data, …» clause.
 *
 * Still NOT «Дедлайн ЕВИ» (link + 10 days) — a different clock about a different
 * moment.
 */
export const CONTRACT_DEADLINE_DAYS = 21;

/**
 * ADDITIVE (v1.9.64, T.Z195) — the `title` the deadline chip carries, hoisted out of
 * the register's page component so the cabinet's twin chip (the curator's own
 * «Дедлайн договора» cell, T.Z195) prints the SAME sentence instead of a drifting
 * copy: the clock's source (the УНТИ auto-decline date) and its fallback, in one line.
 */
export const CONTRACT_DEADLINE_CHIP_TITLE = `Дедлайн договора — дата автоотклонения в УНТИ (без данных УНТИ — ${CONTRACT_DEADLINE_DAYS} календарных дней с прохождения ЕВИ, день дедлайна входит)`;

/** The three states the cell/chip can render. */
export type ContractDeadlineTone = 'signed' | 'warn' | 'danger';

/**
 * The running-clock branch of {@link ContractDeadlineView}: the api's day count and
 * due date, formatted — `overdue` is `daysLeft < 0`, nothing else is re-derived.
 */
export interface ContractDeadlineRunning {
  tone: 'warn' | 'danger';
  /** The due moment as the api answered it (ISO). */
  at: string;
  /** The api's whole-day count — negative = overdue, `0` = the deadline is today. */
  daysLeft: number;
  overdue: boolean;
  /** `«25.09.2026 · через 3 дн.»` / `«25.09.2026 · сегодня»` / `«… · просрочен на 2 дн.»` */
  label: string;
}

/**
 * The signed branch: the terminal state has no deadline left to say — the calm text
 * carries the signing day the «Договор» pill next to it already announces.
 */
export interface ContractDeadlineSigned {
  tone: 'signed';
  /** `«Подписан 10.09.2026»`, or the bare word when the platform kept no instant. */
  label: string;
}

export type ContractDeadlineView = ContractDeadlineRunning | ContractDeadlineSigned;

/**
 * The «Дедлайн договора» cell of the register and the drawer chip's view, `null` =
 * «—» (no clock): the row is signed → the calm «Подписан dd.mm.yyyy» state; the api
 * answers a running deadline (`EVI_PASSED` + unsigned — the УНТИ auto-decline date,
 * or the pass+21 fallback when the mirror has none) → the warn («через N дн.») or
 * danger («сегодня» / «просрочен на N дн.») state; anything else — another funnel
 * status, or neither source holds a date — has no deadline to show.
 *
 * Pure and a function of the row alone (no `Date.now()`): the api already spent the
 * request instant, and the render must not re-spend it.
 */
export function contractDeadlineView(
  application: Pick<
    ApplicationDto,
    'contractState' | 'contractSignedAt' | 'contractDeadlineAt' | 'contractDaysLeft'
  >,
): ContractDeadlineView | null {
  // The terminal state first: a signed row answers null on both deadline keys, and
  // the pill next to it already says «Подписан» — the date here is the calm echo.
  if (application.contractState === 'SIGNED') {
    const day =
      application.contractSignedAt !== null ? ` ${formatDateOnly(application.contractSignedAt)}` : '';
    return { tone: 'signed', label: `Подписан${day}` };
  }
  // An absent key reads exactly like a null one (the api always serialises both, but
  // a hand-built row or a stub that predates v1.9.46 must not read as a running clock
  // — the `address?` precedent of the drawer).
  if (application.contractDeadlineAt == null || application.contractDaysLeft == null) return null;

  const daysLeft = application.contractDaysLeft;
  const date = formatDateOnly(application.contractDeadlineAt);
  if (daysLeft < 0) {
    return {
      tone: 'danger',
      at: application.contractDeadlineAt,
      daysLeft,
      overdue: true,
      label: `${date} · просрочен на ${Math.abs(daysLeft)} дн.`,
    };
  }
  if (daysLeft === 0) {
    return { tone: 'danger', at: application.contractDeadlineAt, daysLeft, overdue: false, label: `${date} · сегодня` };
  }
  return {
    tone: 'warn',
    at: application.contractDeadlineAt,
    daysLeft,
    overdue: false,
    label: `${date} · через ${daysLeft} дн.`,
  };
}

/** `dd.mm.yyyy` in UTC — the same convention the export's day cells use. */
export function formatDateOnly(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const p2 = (value: number): string => String(value).padStart(2, '0');
  return `${p2(date.getUTCDate())}.${p2(date.getUTCMonth() + 1)}.${date.getUTCFullYear()}`;
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.8.8, T.Z43) — «Программа» and «Аудитория»
// ---------------------------------------------------------------------------

/**
 * The `?audience=` vocabulary of #3, imported from the ONE shared definition — the api
 * resolves it through the same fold, and the cabinet's filter (T.Z43) takes the same
 * three values, so a fourth spelling of «СПО» can never appear in one of the three
 * readers.
 */
export const AUDIENCE_FILTER_OPTIONS: ReadonlyArray<string> = AUDIENCE_FILTERS;

/** RU labels of the three audiences — the register's select prints them verbatim. */
export const AUDIENCE_LABEL: Readonly<Record<string, string>> = {
  '8-9': '8–9 классы',
  '10-11': '10–11 классы',
  'СПО': 'СПО',
};

/** `спо` → `СПО`; anything the api would 400 never leaves the browser. */
export function parseAudienceParam(value: string | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const candidate = audienceFilterOf(value);
  return candidate ?? undefined;
}

/**
 * The «Программа» combobox's list: the DISTINCT `programs.short_name` values of the
 * dictionary (#21 — what the api resolves `?programShort=` against), each hinting the
 * «программа + аудитория» pair it stands for. A short name covers the офл/онл PAIR of
 * one course, so the dictionary's 12 rows fold to 6 options; a course with no short
 * name contributes nothing (the id filter `?courseId=` remains the precise tool).
 *
 * Order: first appearance in the dictionary (the seed's офл-block then онл-block),
 * which keeps the six captions in the owner's own order — stable, no re-sorting a
 * Cyrillic alphabet the sheet does not follow.
 */
export function programShortOptions(
  programs: ReadonlyArray<{ shortName?: string | null; name: string; audience: string | null }>,
): ComboboxOption[] {
  const byValue = new Map<string, ComboboxOption>();
  for (const program of programs) {
    const shortName = program.shortName?.trim() ?? '';
    if (shortName === '' || byValue.has(shortName)) continue;
    const bucket = audienceBucketOf(program.audience);
    byValue.set(shortName, {
      value: shortName,
      label: shortName,
      hint: bucket === null ? program.name : `${program.name} · ${AUDIENCE_LABEL[bucket] ?? bucket}`,
    });
  }
  return [...byValue.values()];
}

// --- the venue filter's «Без площадки» state (T.Z20) -------------------------
//
// The venue filter is a combobox over the venue dictionary (#6), so every option
// value it used to emit was a venue id. The «Без площадки» item the owner asked
// for is NOT a venue — it is the contract's own `?hasVenue=false` bucket — and it
// leads the list (a manager hunts the unlinked applicants first, the dictionary
// below). Because a venue id and this state share one control, both halves of the
// model live here: what the combobox prints, and the URL patch a commit produces.

/**
 * The combobox token of the «Без площадки» state. Deliberately non-numeric: a venue
 * id is always a digit string, so no real option can ever collide with it (the same
 * trick `manager-statuses-model.ts` plays with its `none` token).
 */
export const VENUE_FILTER_NONE = 'none';

/** The RU label of that state — the top item of the venue filter's list. */
export const NO_VENUE_LABEL = 'Без площадки';

/** The option as the combobox takes it — prepended by the filter, never part of #6. */
export const NO_VENUE_OPTION: ComboboxOption = { value: VENUE_FILTER_NONE, label: NO_VENUE_LABEL };

/**
 * What the venue combobox prints for a URL: the «Без площадки» token when the URL
 * carries `hasVenue=false` (and no venue — a link that carries both shows the venue,
 * the more specific of the two, exactly how `parseManagerStatusFilter` folds its pair),
 * else the venue id as text, else `''` (nothing selected).
 */
export function venueFilterValue(params: Record<string, string>): string {
  const venueId = parseIntParam(params[PARAM.VENUE_ID]);
  if (venueId !== undefined) return String(venueId);
  return parseBoolParam(params[PARAM.HAS_VENUE]) === false ? VENUE_FILTER_NONE : '';
}

/**
 * The URL patch one venue-filter commit produces. The two states are mutually
 * exclusive on the wire — `venueId=N` AND `hasVenue=false` is an empty set for every
 * N — so a commit always clears the other key, in ONE URL write (the same reason the
 * manager-status filter patches instead of committing twice). The keys are the
 * contract's own: `venueId` and `hasVenue`.
 */
export function venueFilterPatch(value: string): Record<string, string | undefined> {
  const trimmed = value.trim();
  if (trimmed === VENUE_FILTER_NONE) {
    return { [PARAM.HAS_VENUE]: 'false', [PARAM.VENUE_ID]: undefined };
  }
  const venueId = parseIntParam(trimmed);
  if (venueId !== undefined) {
    return { [PARAM.VENUE_ID]: String(venueId), [PARAM.HAS_VENUE]: undefined };
  }
  // `''` (the combobox's clear button) and anything else: show everything.
  return { [PARAM.VENUE_ID]: undefined, [PARAM.HAS_VENUE]: undefined };
}

function trimParam(value: string | undefined): string | undefined {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed === '' ? undefined : trimmed;
}

// ---------------------------------------------------------------------------
// ADDITIVE (T.Z16) — the venue-filter option list: «Без площадки» leads it
// ---------------------------------------------------------------------------

/** The bucket's secondary line — the one sentence that says what it holds. */
export const VENUE_FILTER_NONE_HINT = 'Заявления, которым площадка ещё не назначена';

/**
 * The venue combobox's whole list: «Без площадки» FIRST (top of the list, above
 * every specific venue — the owner's placement), then the directory in its own
 * order. The same rows the register's drill-downs and the api's `#3` accept; the
 * bucket's state and its URL patch are the T.Z20 pair right above.
 */
export function venueFilterOptions(
  entries: ReadonlyArray<{ id: number; name: string; address?: string | null }>,
): ComboboxOption[] {
  return [
    { value: VENUE_FILTER_NONE, label: NO_VENUE_LABEL, hint: VENUE_FILTER_NONE_HINT },
    ...venueComboboxOptions(entries),
  ];
}

/** URL params → filters. Anything the api would reject never leaves the browser. */
export function parseFilters(params: Record<string, string>): ApplicationFilters {
  const page = parseIntParam(params[PARAM.PAGE]) ?? 1;
  const pageSize = Math.min(parseIntParam(params[PARAM.PAGE_SIZE]) ?? DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE);
  return {
    status: parseStatusParam(params[PARAM.STATUS]),
    venueId: parseIntParam(params[PARAM.VENUE_ID]),
    courseId: parseIntParam(params[PARAM.COURSE_ID]),
    // ADDITIVE (v1.8.8, T.Z43) — an audience the api would 400 never leaves the browser;
    // a programShort rides verbatim (the api answers 400 for one it cannot resolve).
    programShort: trimParam(params[PARAM.PROGRAM_SHORT]),
    audience: parseAudienceParam(params[PARAM.AUDIENCE]),
    // ADDITIVE (v1.8.9, T.Z56) — a hand-edited value falls back to «Все», never to a
    // request the api would 400.
    department: parseDepartmentBucket(params[PARAM.DEPARTMENT]),
    region: trimParam(params[PARAM.REGION]),
    format: parseFormatParam(params[PARAM.FORMAT]),
    // ADDITIVE (v1.9.21, T.Z101) — a hand-edited value falls back to «Все», never to
    // a request the api would 400.
    contract: parseContractParam(params[PARAM.CONTRACT]),
    // ADDITIVE (v1.9.49, T.Z169) — a junk value degrades to «no filter», like every
    // boolean param here.
    legalRepConsent: parseLegalRepConsentParam(params[PARAM.LEGAL_REP_CONSENT]),
    // ADDITIVE (v1.9.51, T.Z172) — a junk value degrades to «no filter», like every
    // boolean param here.
    signerMatches: parseSignerMatchesParam(params[PARAM.SIGNER_MATCHES]),
    // ADDITIVE (v1.9.52, T.Z176) — a junk value degrades to «no filter», like every
    // boolean param here.
    enrolled: parseEnrolledParam(params[PARAM.ENROLLED]),
    // ADDITIVE (v1.10.0, T.Z249 / TEC-1415) — a junk value degrades to «no filter»,
    // like every boolean param here.
    distributed: parseDistributedParam(params[PARAM.DISTRIBUTED]),
    // ADDITIVE (v1.9.25, T.Z113) — a junk value degrades to «no filter», never to a
    // request the api would 400 (the same reading every boolean param follows).
    eviFailed: parseBoolParam(params[PARAM.EVI_FAILED]),
    // ADDITIVE (v1.9.48, T.Z168) — a hand-edited value falls back to «Все», never to
    // a request the api would 400 (the same reading `contract` follows).
    ageGroup: parseAgeGroupParam(params[PARAM.AGE_GROUP]),
    // ADDITIVE (v1.9.56, T.Z186) — a hand-typed id the index does not know still
    // travels (an honest empty page is the api's answer); only junk is dropped here.
    mailingList: parseIntParam(params[PARAM.MAILING_LIST]),
    // ADDITIVE (v1.9.56, T.Z186) — a junk value degrades to «no filter», like every
    // boolean param here.
    ftp: parseFtpParam(params[PARAM.FTP]),
    // ADDITIVE (v1.9.66, T.Z203) — a hand-edited literal (`FOUND`, anything unknown)
    // degrades to «Все», never to a request the api would 400 (the `contract`
    // reading: the api's literals are CASE-SENSITIVE, so the browser folds instead).
    untiApi: parseUntiApiParam(params[PARAM.UNTI_API]),
    // ADDITIVE (v1.9.87, T.Z237) — rides verbatim (the `programShort` reading: the
    // vocabulary is data; a hand-edited word is the api's 400 to explain).
    transfert: parseTransfertParam(params[PARAM.TRANSFERT]),
    q: trimParam(params[PARAM.Q]),
    hasVenue: parseBoolParam(params[PARAM.HAS_VENUE]),
    // ADDITIVE (v1.8.0, Р9/#3, T.Y10) — the parser folds the two keys to never send both.
    managerStatus: parseManagerStatusFilter(params),
    page: Math.max(page, 1),
    pageSize,
    // ADDITIVE (v1.9.3, T.Z59) — the header-click pair, with the legacy `sort=` read
    // as a fallback so a bookmarked link keeps its meaning.
    sort: parseSortState(params),
  };
}

/** The api query of #3 — defaults dropped, so an unfiltered view sends `?`-nothing. */
export function filtersToQuery(filters: ApplicationFilters): ApplicationListQuery {
  const query: ApplicationListQuery = {};
  // ADDITIVE (v1.9.33, T.Z141) — the list travels as the array; `buildQuery` stringifies
  // it into the wire's comma spelling (`status=LINK_RECEIVED,EVI_PASSED`).
  if (filters.status?.length) query.status = filters.status;
  if (filters.venueId !== undefined) query.venueId = filters.venueId;
  if (filters.courseId !== undefined) query.courseId = filters.courseId;
  // ADDITIVE (v1.8.8, T.Z43) — both compose server-side with everything else, including
  // an explicit `courseId` (the api ANDs the resolved set with the id).
  if (filters.programShort) query.programShort = filters.programShort;
  if (filters.audience) query.audience = filters.audience;
  // ADDITIVE (v1.8.9, T.Z56) — composes server-side with everything else, including an
  // explicit `venueId` (the api ANDs the department's venue set with the id).
  if (filters.department) query.department = filters.department;
  if (filters.region) query.region = filters.region;
  // ADDITIVE (v1.9.19, T.Z99) — `none` travels as the literal, the api's own spelling.
  if (filters.format) query.format = filters.format;
  // ADDITIVE (v1.9.21, T.Z101) — the literal is the api's own spelling, so #3 and the
  // export both take it unchanged.
  if (filters.contract) query.contract = filters.contract;
  // ADDITIVE (v1.9.49, T.Z169) — `false` MUST travel (the «Не отмечено» view the
  // owner's case needs); only `undefined` means no filter.
  if (filters.legalRepConsent !== undefined) query.legalRepConsent = filters.legalRepConsent;
  // ADDITIVE (v1.9.51, T.Z172) — `false` MUST travel (the «Нет» view the audit needs);
  // only `undefined` means no filter.
  if (filters.signerMatches !== undefined) query.signerMatches = filters.signerMatches;
  // ADDITIVE (v1.9.52, T.Z176) — `false` MUST travel (the «Нет» view); only
  // `undefined` means no filter.
  if (filters.enrolled !== undefined) query.enrolled = filters.enrolled;
  // ADDITIVE (v1.10.0, T.Z249 / TEC-1415) — `false` MUST travel (the «Не
  // распределён» view the обзвон works from); only `undefined` means no filter.
  if (filters.distributed !== undefined) query.distributed = filters.distributed;
  // ADDITIVE (v1.9.25, T.Z113) — `false` MUST travel (the explicitly-not-failed view);
  // only `undefined` means no filter.
  if (filters.eviFailed !== undefined) query.eviFailed = filters.eviFailed;
  // ADDITIVE (v1.9.48, T.Z168) — the literal is the api's own spelling, so #3 and the
  // export both take it unchanged.
  if (filters.ageGroup) query.ageGroup = filters.ageGroup;
  // ADDITIVE (v1.9.56, T.Z186) — the list id and the ftp literal are the api's own
  // spellings, so #3 and the export both take them unchanged.
  if (filters.mailingList !== undefined) query.mailingList = filters.mailingList;
  if (filters.ftp !== undefined) query.ftp = filters.ftp;
  // ADDITIVE (v1.9.66, T.Z203) — the literal is the api's own spelling, so #3 and the
  // export both take it unchanged.
  if (filters.untiApi) query.untiApi = filters.untiApi;
  // ADDITIVE (v1.9.87, T.Z237) — `none` and every facet word travel verbatim; #18
  // takes the same key, so the export button sends it too.
  if (filters.transfert) query.transfert = filters.transfert;
  if (filters.q) query.q = filters.q;
  if (filters.hasVenue !== undefined) query.hasVenue = filters.hasVenue;
  // ADDITIVE (v1.8.0, Р9/#3, T.Y10) — one key at a time: `managerStatus=none` and
  // `managerStatusId` together would answer 400 (the journal cannot both carry and
  // not carry an active status).
  if (filters.managerStatus?.kind === 'none') query.managerStatus = 'none';
  else if (filters.managerStatus?.kind === 'id') query.managerStatusId = filters.managerStatus.id;
  if (filters.page > 1) query.page = filters.page;
  if (filters.pageSize !== DEFAULT_PAGE_SIZE) query.pageSize = filters.pageSize;
  // ADDITIVE (v1.9.3, T.Z59) — the header-click pair; the default state travels as
  // nothing at all, so the request (and the URL) stay byte-compatible with the
  // pre-T.Z59 default view.
  const sort = formatSortState(filters.sort);
  if (sort) {
    query.sortBy = sort.sortBy;
    query.order = sort.order;
  }
  return query;
}

/** URL params of a filter set — the reset / link form of {@link filtersToQuery}. */
export function filtersToParams(filters: ApplicationFilters): Record<string, string> {
  const params: Record<string, string> = {};
  const query = filtersToQuery(filters);
  for (const [key, value] of Object.entries(query)) params[key] = String(value);
  return params;
}

/**
 * ADDITIVE (v1.8.0, Р9, T.Y10) — the export query of the view on screen (#18).
 *
 * The pagination keys never travel (an export is the whole filtered set — the api
 * would answer 400 to a `page`).
 *
 * ADDITIVE (v1.9.79, T.Z220) — the drops RETIRED: #18 takes the register's COMPLETE
 * filter grammar now (the api's own parity pass), so the manager-status pair and the
 * «Отдел» bucket ride to the file like every other key — before this they were
 * dropped on purpose (`#18's allowlist does not know them yet`), and a
 * manager-status- or department-filtered view exported UNFILTERED by them. The
 * ЕПГУ `format` still travels under its export-only key `appFormat` — the mapping
 * lives in the page's `exportApplicationsParams`, which now forwards the `none`
 * literal too (the api takes it since v1.9.79).
 */
export function exportApplicationsQuery(
  query: ApplicationListQuery,
): Record<string, string | number | boolean | undefined> {
  const {
    page: _page,
    pageSize: _pageSize,
    sort: _sort,
    // ADDITIVE (v1.9.3, T.Z59) — dropped like `sort`: #18's allowlist did not know the
    // keys and answered 400, and an export is the whole filtered set (the file's own
    // row order was the writer's, not the screen's).
    // ADDITIVE (v1.9.77, T.Z218) — the exception grew: #18 honours the grade sorts,
    // so a register sorted by a УНТИ grade column exports IN THAT ORDER. Widened
    // ADDITIVE (v1.9.78, T.Z219) from the one `gradeSum` literal to all FOUR
    // (`gradeSum` + the three individual grades) — the api's own #18 acceptance.
    // Any other column's sort is still dropped — the file keeps the writer's default
    // rather than quietly ignoring the sort.
    sortBy: rawSortBy,
    order: rawOrder,
    ...filters
  } = query;
  // The spread carries the widened `status` (array or single) — cast to the scalar
  // record here, the next line folds the one array value into its wire spelling.
  const exported = { ...filters } as Record<string, string | number | boolean | undefined>;
  // ADDITIVE (v1.9.33, T.Z141) — the status LIST travels in its own wire spelling (the
  // api splits the comma form back into the same `IN`), so the export URL stays scalar.
  if (Array.isArray(exported.status)) exported.status = exported.status.join(',');
  // ADDITIVE (v1.9.77, T.Z218; widened v1.9.78, T.Z219) — the sort rides to the file
  // when it is one of the FOUR grade orders #18 honours ({@link EXPORT_SORT_FIELDS},
  // the api's own #18 vocabulary); any other value degrades to no sort, never to a
  // request the api would 400 (the `parseSortState` rule).
  if ((EXPORT_SORT_FIELDS as readonly string[]).includes(rawSortBy ?? '')) {
    exported.sortBy = rawSortBy;
    if (rawOrder !== undefined) exported.order = rawOrder;
  }
  return exported;
}

/** `true` when any filter differs from the default view (drives the reset button). */
export function hasActiveFilters(filters: ApplicationFilters): boolean {
  return (
    filters.status !== undefined ||
    filters.venueId !== undefined ||
    filters.courseId !== undefined ||
    // ADDITIVE (v1.8.8, T.Z43)
    filters.programShort !== undefined ||
    filters.audience !== undefined ||
    // ADDITIVE (v1.8.9, T.Z56)
    filters.department !== undefined ||
    filters.region !== undefined ||
    filters.format !== undefined ||
    // ADDITIVE (v1.9.21, T.Z101)
    filters.contract !== undefined ||
    // ADDITIVE (v1.9.49, T.Z169) — `false` is an active view too (an explicit «no»).
    filters.legalRepConsent !== undefined ||
    // ADDITIVE (v1.9.51, T.Z172) — `false` is an active view too (an explicit «нет»).
    filters.signerMatches !== undefined ||
    // ADDITIVE (v1.9.52, T.Z176) — `false` is an active view too (an explicit «нет»).
    filters.enrolled !== undefined ||
    // ADDITIVE (v1.10.0, T.Z249 / TEC-1415) — `false` is an active view too (an
    // explicit «нет», the обзвон queue).
    filters.distributed !== undefined ||
    // ADDITIVE (v1.9.25, T.Z113) — `false` is an active view too (an explicit «no»).
    filters.eviFailed !== undefined ||
    // ADDITIVE (v1.9.48, T.Z168) — any chosen bucket is an active view.
    filters.ageGroup !== undefined ||
    // ADDITIVE (v1.9.56, T.Z186) — a chosen list and either «FTP» answer (incl. «Нет»)
    // are active views too.
    filters.mailingList !== undefined ||
    filters.ftp !== undefined ||
    // ADDITIVE (v1.9.66, T.Z203) — any chosen «УНТИ API» bucket is an active view.
    filters.untiApi !== undefined ||
    // ADDITIVE (v1.9.87, T.Z237) — any chosen «Трансферт» value (`none` included)
    // is an active view too.
    filters.transfert !== undefined ||
    filters.q !== undefined ||
    filters.hasVenue !== undefined ||
    // ADDITIVE (v1.8.0, Р9/#3, T.Y10)
    filters.managerStatus !== undefined ||
    filters.page > 1 ||
    filters.pageSize !== DEFAULT_PAGE_SIZE ||
    // ADDITIVE (v1.9.3, T.Z59) — a non-default sort counts as an active view, like the
    // rest; the default state (updatedAt desc) does not.
    !isDefaultSort(filters.sort)
  );
}

// ---------------------------------------------------------------------------
// T.Z192 — the «applied» hint: the visual marker of a control that narrows the view
// ---------------------------------------------------------------------------

/**
 * The class a filter control carries while its URL value is non-default (T.Z192,
 * owner 2026-09-25: the register grew too many filters to eyeball which are set).
 * `applications.css` paints it with the palette's own warm tint — `--brand-soft`,
 * already re-mapped for the dark theme, so one rule covers both. Purely visual:
 * no behavior, no request, no contract rides on it.
 */
export const FILTER_APPLIED_CLASS = 'applications-filter--applied';

/**
 * `{@link FILTER_APPLIED_CLASS}` when `value` is a NON-DEFAULT filter value,
 * `undefined` otherwise — the `className` a control passes straight through
 * (`Input`/`Select` join it to `.input`, the Combobox carries it on its root).
 *
 * Applied means exactly what {@link hasActiveFilters} reads per axis, one control
 * at a time: a non-empty string (every URL param here — including `hasVenue=false`
 * and the literal `none` buckets, which travel as strings and are an explicit
 * choice), a non-empty array (the `?status=A,B` preset) or a DEFINED boolean
 * (`true` and `false` are both answers; `undefined` is «no filter»). The
 * placeholder option (`''` — «Все…» / «Любая») is never applied, so the «Сбросить»
 * button clears the tints by clearing the state they derive from — no extra code.
 * Pure, so the plain-Node unit project pins it.
 */
export function filterAppliedClass(
  value: string | readonly string[] | boolean | null | undefined,
): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'boolean') return FILTER_APPLIED_CLASS;
  return value.length > 0 ? FILTER_APPLIED_CLASS : undefined;
}

/** `Math.ceil(total / pageSize)`, at least 1 — an empty result still shows «1». */
export function pageCount(total: number, pageSize: number): number {
  const size = pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE;
  return Math.max(1, Math.ceil(Math.max(total, 0) / size));
}

/** A page that ran past the end (after a filter shrank the result) is clamped back. */
export function clampPage(page: number, total: number, pageSize: number): number {
  return Math.min(Math.max(page, 1), pageCount(total, pageSize));
}

// ---------------------------------------------------------------------------
// Profile links
// ---------------------------------------------------------------------------

/** The full-page profile route (`/applications/:pguId`) the router mounts. */
export function profilePath(pguId: string): string {
  return `/applications/${encodeURIComponent(pguId)}`;
}

/**
 * The drawer form of the same profile: it rides on the list URL, so closing it
 * restores the exact filter state the manager came from.
 */
export function profileSearch(pguId: string, filters?: ApplicationFilters): string {
  const search = new URLSearchParams(filtersToParams(filters ?? DEFAULT_FILTERS));
  search.set(PARAM.PGU_ID, pguId);
  return `?${search.toString()}`;
}

/** `^[78]\d{9}$` — the api 400s anything else (docs/05 §3); the route refuses it
 * earlier. `8`+9 digits is the provider's id series since ≈01.09.2026 (T2.1b) — without
 * it every September application detail route would render the error state. */
export const PGU_ID_PATTERN = /^[78]\d{9}$/;

/** Route/URL param → a pguId the api will accept, else `null` (renders an error state). */
export function parsePguId(value: string | undefined): string | null {
  return typeof value === 'string' && PGU_ID_PATTERN.test(value) ? value : null;
}

/** The short id a row shows when `pgu_id` never arrived (the FTP-only row). */
export function rowKey(application: ApplicationDto): string {
  return application.pguId ?? `unti:${application.untiId ?? 'unknown'}`;
}

// ---------------------------------------------------------------------------
// Field metadata: labels, sources, the owner table
// ---------------------------------------------------------------------------

/** Where a field's value comes from (docs/05 §4.1 field-ownership table). */
export type FieldSource = 'EPGU' | 'EVI' | 'FTP' | 'MATCHER' | 'MANUAL' | 'ENGINE';

export const SOURCE_LABEL: Readonly<Record<FieldSource, string>> = {
  EPGU: 'ЕПГУ',
  EVI: 'ЕВИ',
  FTP: 'УНТИ',
  MATCHER: 'Матчер',
  MANUAL: 'Вручную',
  /** The status is never written by a handler — StatusEngine only. */
  ENGINE: 'Статус-движок',
};

export const FIELD_SOURCE: Readonly<Record<string, FieldSource>> = {
  pguId: 'EPGU',
  epguStatusCode: 'EPGU',
  courseId: 'EPGU',
  region: 'EPGU',
  // T.F1 — the rest of the ЕПГУ row: ЕПГУ owns every one of them.
  submittedBy: 'EPGU',
  submittedAt: 'EPGU',
  statusUpdatedAt: 'EPGU',
  isSpoStudent: 'EPGU',
  gender: 'EPGU',
  classCourse: 'EPGU',
  specialty: 'EPGU',
  specialtyCode: 'EPGU',
  direction: 'EPGU',
  orgName: 'EPGU',
  courseName: 'EPGU',
  format: 'EPGU',
  regionConfirmed: 'EPGU',
  fullName: 'EVI',
  phone: 'EVI',
  email: 'EVI',
  untiId: 'FTP',
  eviPassedAt: 'FTP',
  venueId: 'MATCHER',
  venueName: 'MATCHER',
  venueMatchStatus: 'MATCHER',
  status: 'ENGINE',
  firstSeenAt: 'EVI',
  linkReceivedAt: 'EVI',
  updatedAt: 'ENGINE',
  isOrganic: 'MANUAL',
  agreementRejected: 'MANUAL',
  // ADDITIVE (v1.9.15, T.Z96) — the manual «не прошёл ЕВИ» flag: a human fact.
  eviFailedManually: 'MANUAL',
  // ADDITIVE (v1.9.25, T.Z113) — the auto «Провалил ЕВИ» fact: the ЕВИ file said so
  // (the worker journals it with actor SYSTEM, source SNAPSHOT — the file's own path).
  eviFailed: 'ENGINE',
  // ADDITIVE (v1.9.21, T.Z101) — the contract facts: the external platform said so,
  // exactly the FTP side of the funnel (the worker journals it with actor SYSTEM).
  contractStatus: 'FTP',
};

export const FIELD_LABEL: Readonly<Record<string, string>> = {
  pguId: 'ЕПГУ ID',
  untiId: 'УНТИ ID',
  courseId: 'Курс',
  venueId: 'Площадка',
  venueName: 'Площадка',
  venueMatchStatus: 'Сопоставление',
  region: 'Регион',
  fullName: 'ФИО',
  phone: 'Телефон',
  email: 'Email',
  epguStatusCode: 'Статус ЕПГУ (сырой)',
  status: 'Статус',
  firstSeenAt: 'В системе с',
  linkReceivedAt: 'Ссылка отправлена',
  eviPassedAt: 'ЕВИ пройдено',
  updatedAt: 'Обновлено',
  isOrganic: 'Органик',
  agreementRejected: 'Отказ от договора',
  // ADDITIVE (v1.9.15, T.Z96) — the journal names the manual fact, not the derived status.
  eviFailedManually: 'Не прошёл ЕВИ (вручную)',
  // ADDITIVE (v1.9.25, T.Z113) — the auto fact's journal entry: what happened and why,
  // in one phrase (the FLAG_SET rows the worker writes on the field `eviFailed`).
  eviFailed: 'Провалил ЕВИ (авто, исчез из файла ЕВИ)',
  // ADDITIVE (v1.9.21, T.Z101) — the contract sync's journal entry (a synthetic field
  // carrying the JSON payload, the `venueMatchEvidence` trick).
  contractStatus: 'Договор',
  // ADDITIVE (v1.9.49, T.Z169) — the same sync's consent entry, the third fact axis.
  legalRepConsent: 'Получено согласие родителей',
  // ADDITIVE (T.Z178) — the same sync's signer-CLEARING entry (the axis journals
  // nothing on a fill; a re-homed leaf journals the verdict-only reversal here).
  signatory: 'Договор заключен ПП?',
  // T.F1 — the ЕПГУ source block, under its sheet names.
  submittedBy: 'Заявление подал',
  submittedAt: 'Дата подачи',
  statusUpdatedAt: 'Дата статуса',
  isSpoStudent: 'Студент СПО',
  gender: 'Пол',
  classCourse: 'Класс/курс обучения',
  specialty: 'Специальность',
  specialtyCode: 'Код специальности',
  direction: 'Направление',
  orgName: 'Образовательная организация',
  courseName: 'Наименование курса',
  format: 'Формат',
  regionConfirmed: 'Выбранный регион обучения',
  // T.Z45 — the duplicate-ЕПГУ pass's audit payload (a synthetic field, not a column).
  autoDuplicateRejection: 'Авто-отказ (дубль ЕПГУ)',
};

/**
 * T.Z45 — the synthetic `application_events.field` the snapshot apply's duplicate
 * pass writes its audit payload on (`newValue` = JSON, the `venueMatchEvidence`
 * trick). Mirrors `AUTO_DUPLICATE_REJECTION_FIELD` in
 * `apps/mail-worker/src/registry/registry-status-engine.ts` — the worker writes
 * it, this drawer reads it, and the two must never spell it differently.
 */
export const AUTO_DUPLICATE_REJECTION_FIELD = 'autoDuplicateRejection';

/**
 * ADDITIVE (v1.9.21, T.Z101) — the synthetic `application_events.field` the worker's
 * contract sync journals its fact changes on (`oldValue`/`newValue` = the same JSON
 * payload shape). Mirrors `CONTRACT_STATUS_EVENT_FIELD` in
 * `apps/mail-worker/src/contract/contract-sync.service.ts` — the worker writes it,
 * this drawer reads it.
 */
export const CONTRACT_STATUS_FIELD = 'contractStatus';

/** One payload of a {@link CONTRACT_STATUS_FIELD} journal row. */
export interface ContractStatusPayload {
  arrived: boolean;
  signed: boolean;
  signedAt: string | null;
  notArrived: boolean;
}

/**
 * The readable sentence for one contract-sync payload, the way the drawer's
 * «История изменений» renders it: the state + the platform's signing day. An
 * unparsable payload falls back to the raw string rather than hiding the row (the
 * `autoDuplicateRejection` rule).
 */
export function contractStatusChangeText(raw: string | null): string | null {
  if (raw === null || raw.trim() === '') return null;
  try {
    const payload = JSON.parse(raw) as Partial<ContractStatusPayload>;
    if (payload.signed === true) {
      const day = typeof payload.signedAt === 'string' ? ` ${formatDateOnly(payload.signedAt)}` : '';
      return `Договор подписан${day}`;
    }
    if (payload.notArrived === true) return 'Не пришёл в iSmart';
    if (payload.arrived === true) return 'Пришёл в iSmart, договор не подписан';
    return 'Договор синк сброшен (нет данных о ребёнке на платформе)';
  } catch {
    return raw;
  }
}

/**
 * ADDITIVE (v1.9.49, T.Z169) — the synthetic `application_events.field` the worker's
 * contract sync journals the consent moves on (the same JSON-payload trick). Mirrors
 * `LEGAL_REP_CONSENT_EVENT_FIELD` in `apps/mail-worker/src/contract/
 * contract-sync.service.ts` — the worker writes it, this drawer reads it.
 */
export const LEGAL_REP_CONSENT_FIELD = 'legalRepConsent';

/** One payload of a {@link LEGAL_REP_CONSENT_FIELD} journal row. */
export interface LegalRepConsentPayload {
  consent: boolean | null;
}

/**
 * The readable sentence for one consent-sync payload, the way the drawer's «История
 * изменений» renders it. An unparsable payload falls back to the raw string rather
 * than hiding the row (the `autoDuplicateRejection` rule).
 */
export function legalRepConsentChangeText(raw: string | null): string | null {
  if (raw === null || raw.trim() === '') return null;
  try {
    const payload = JSON.parse(raw) as Partial<LegalRepConsentPayload>;
    if (payload.consent === true) return 'Согласие родителей отмечено на платформе';
    if (payload.consent === false) return 'Согласие родителей не отмечено на платформе';
    return 'Согласие родителей: нет данных (ребёнка нет на платформе)';
  } catch {
    return raw;
  }
}

/**
 * ADDITIVE (T.Z178) — the synthetic `application_events.field` the worker's contract
 * sync journals the signer-PAIR CLEARING on — the only journal row the signer axis
 * ever gets (a fill journals nothing: the columns carry a person's name). A re-homed
 * leaf (the facts follow the (ЕПГУ+УНТИ) pair) strips the name off the twin it leaves,
 * and the timeline shows that reversal. Mirrors `SIGNATORY_EVENT_FIELD` in
 * `apps/mail-worker/src/contract/contract-sync.service.ts` — the worker writes it,
 * this drawer reads it. The payload names the FIELD only, never the name value.
 */
export const SIGNATORY_FIELD = 'signatory';

/** One payload of a {@link SIGNATORY_FIELD} journal row — the verdict, no name. */
export interface SignatoryPayload {
  matches: boolean | null;
}

/**
 * The readable word for one signatory-clearing payload. `matches` is the stored
 * verdict (`signer_matches`); the clearing itself writes `null`, so a real reversal
 * reads «Да/Нет → —» and nothing about WHO signed ever renders from the journal.
 */
export function signatoryChangeText(raw: string | null): string | null {
  if (raw === null || raw.trim() === '') return null;
  try {
    const payload = JSON.parse(raw) as Partial<SignatoryPayload>;
    if (payload.matches === true) return 'Да';
    if (payload.matches === false) return 'Нет';
    return null;
  } catch {
    return raw;
  }
}

/**
 * The readable sentence for one auto-«Отказ» payload.
 *
 * `action: "reject"` names the SURVIVING application so a manager can open it and
 * judge the grouping by hand; `action: "release"` is the reversal half — the
 * provider fixed the duplicate and the row returns to its derived status. An
 * unparsable payload (an older writer, a hand edit) falls back to the raw string
 * rather than hiding the row.
 */
function autoDuplicateChangeText(newValue: string | null): string | null {
  if (newValue === null) return null;
  try {
    const payload = JSON.parse(newValue) as { action?: string; survivingPguId?: string };
    if (payload.action === 'reject') {
      const survivor = typeof payload.survivingPguId === 'string' ? payload.survivingPguId : '—';
      return `Авто-отказ: дубль заявления — осталось заявление ${survivor}`;
    }
    if (payload.action === 'release') {
      return 'Авто-отказ снят: дубль не подтверждается, статус восстановлен';
    }
  } catch {
    // fall through — the raw value is still better than a blank row
  }
  return newValue;
}

export const EVENT_TYPE_LABEL: Readonly<Record<ApplicationEventType, string>> = {
  STATUS_CHANGED: 'Смена статуса',
  FIELD_UPDATED: 'Изменение поля',
  FLAG_SET: 'Установка флага',
};

export const EVENT_SOURCE_LABEL: Readonly<Record<EventSource, string>> = {
  SNAPSHOT: 'Снапшот',
  FTP: 'УНТИ',
  // ADDITIVE (v1.9.38, T.U1) — the cat.2035.university sweep («скрипт синхронизации
  // УНТИ»); NOT folded into «УНТИ» (FTP): the timeline must distinguish the sources.
  UNTI_API: 'Синк УНТИ (API)',
  MANUAL: 'Вручную',
  SEED: 'Сид',
};

export const EVENT_ACTOR_LABEL: Readonly<Record<EventActorType, string>> = {
  SYSTEM: 'Система',
  USER: 'Пользователь',
};

/** Timeline entry the UI draws — the raw `EventDto` plus its labels. */
export interface TimelineEntry {
  key: string;
  type: ApplicationEventType;
  typeLabel: string;
  source: EventSource;
  sourceLabel: string;
  actorLabel: string;
  fieldLabel: string | null;
  oldValue: string | null;
  newValue: string | null;
  /** `oldValue → newValue`, human-readable; `null` when there is nothing to show. */
  change: string | null;
  createdAt: string;
}

/** The manual flags a MANAGER may flip (contract #5's manual-only fields). */
export const FLAG_FIELDS = ['isOrganic', 'agreementRejected', 'eviFailedManually'] as const;
export type FlagField = (typeof FLAG_FIELDS)[number];

/** `bool → «Да» / «Нет»`, `null → «—»`. */
export function boolLabel(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value ? 'Да' : 'Нет';
}

/** `7912345001` → `7 912 345 001`? No — the id is shown verbatim; a course gets a prefix. */
export function courseLabel(courseId: number | null | undefined): string {
  return courseId === null || courseId === undefined ? '—' : String(courseId);
}

/**
 * `EventDto` → what the timeline renders. Order is the api's (chronological);
 * the only work here is labels and the `old → new` sentence.
 */
export function timelineOf(
  events: ReadonlyArray<{
    type: ApplicationEventType;
    source: EventSource;
    actorType: EventActorType;
    field: string | null;
    oldValue: string | null;
    newValue: string | null;
    createdAt: string;
  }>,
): TimelineEntry[] {
  return events.map((event, index) => {
    const fieldLabel = event.field === null ? null : (FIELD_LABEL[event.field] ?? event.field);
    const oldText = eventValueText(event.field, event.oldValue);
    const newText = eventValueText(event.field, event.newValue);
    // T.Z45: the auto-«Отказ» payload is a JSON blob, not an old → new pair — the
    // drawer spells it out as a sentence instead of printing the JSON.
    const change =
      event.field === AUTO_DUPLICATE_REJECTION_FIELD
        ? autoDuplicateChangeText(event.newValue)
        : // T.Z101: the contract-sync payload is a JSON blob too — the drawer spells
          // the state + the platform's day out instead of printing the JSON.
          event.field === CONTRACT_STATUS_FIELD
          ? contractStatusChangeText(event.newValue)
          : // T.Z169: the consent-sync payload, same blob, same treatment.
            event.field === LEGAL_REP_CONSENT_FIELD
            ? legalRepConsentChangeText(event.newValue)
            : // T.Z178: the signer-CLEARING payload carries the verdict only — the
              // reversal renders as a word pair, never a name.
              event.field === SIGNATORY_FIELD
            ? [signatoryChangeText(event.oldValue) ?? '—', signatoryChangeText(event.newValue) ?? '—'].join(' → ')
            : oldText === null && newText === null
          ? null
          : `${oldText ?? '—'} → ${newText ?? '—'}`;
    return {
      key: `${event.createdAt}-${event.type}-${event.field ?? ''}-${index}`,
      type: event.type,
      typeLabel: EVENT_TYPE_LABEL[event.type],
      source: event.source,
      sourceLabel: EVENT_SOURCE_LABEL[event.source],
      actorLabel: EVENT_ACTOR_LABEL[event.actorType],
      fieldLabel,
      oldValue: event.oldValue,
      newValue: event.newValue,
      change,
      createdAt: event.createdAt,
    };
  });
}

/** A boolean timeline value reads «Да»/«Нет», a venue falls back to its label. */
function eventValueText(field: string | null, value: string | null): string | null {
  if (value === null) return null;
  // T.Z113 — the auto «Провалил ЕВИ» fact is a boolean on the timeline too, like the
  // manual flags: the FLAG_SET rows render «Да»/«Нет», never the raw `true` string.
  if (field === 'isOrganic' || field === 'agreementRejected' || field === 'eviFailedManually' || field === 'eviFailed') {
    return boolLabel(value === 'true');
  }
  // T.Z143 — the virtual status the engine journals on the `status` field reads as the
  // owner's words, never as a raw token. The six STORED statuses keep rendering the
  // raw code they have always rendered here (a pre-existing rendering, pinned by the
  // timeline specs) — only the virtual token has no other human name.
  if (field === 'status' && value === EVI_FAILED_STATUS_TOKEN) {
    return STATUS_FILTER_LABEL[EVI_FAILED_STATUS_TOKEN];
  }
  return value;
}

// ---------------------------------------------------------------------------
// T.F1 — the ЕПГУ source block on the profile
// ---------------------------------------------------------------------------

/** One value of the profile's source-field grid, ready to render. */
export interface ProfileFieldValue {
  /** The `ApplicationDto` key — also the `data-testid` of the cell. */
  field: string;
  /** RU label from the sheet. */
  label: string;
  /** Where the value comes from (the badge the cell shows). */
  source: FieldSource;
  /** The rendered text; `null` means «—». */
  text: string | null;
}

/** One grouped block of the profile card («Заявление» / «Ученик» / «Программа»). */
export interface ProfileFieldGroup {
  key: string;
  title: string;
  fields: ProfileFieldValue[];
}

const PROFILE_TEXT: Partial<Record<string, (application: ApplicationDto) => string | null>> = {
  submittedBy: (application) => application.submittedBy,
  // the two timestamps render «—» through the view's `?? MISSING`, not as a string here
  submittedAt: (application) => (application.submittedAt === null ? null : formatDateTime(application.submittedAt)),
  statusUpdatedAt: (application) =>
    application.statusUpdatedAt === null ? null : formatDateTime(application.statusUpdatedAt),
  isSpoStudent: (application) => (application.isSpoStudent === null ? null : boolLabel(application.isSpoStudent)),
  gender: (application) => application.gender,
  classCourse: (application) => application.classCourse,
  specialty: (application) => application.specialty,
  specialtyCode: (application) => application.specialtyCode,
  direction: (application) => application.direction,
  orgName: (application) => application.orgName,
  courseName: (application) => application.courseName,
  format: (application) => (application.format === null ? null : FORMAT_LABEL[application.format]),
  regionConfirmed: (application) => application.regionConfirmed,
};

/**
 * The profile's source fields, grouped the way a manager reads them (T.F1):
 *   «Заявление» — who filed it, when, and where the ЕПГУ status stands;
 *   «Ученик»    — СПО, пол, класс/курс, специальность + код;
 *   «Программа» — направление, курс, формат, организация, регион.
 *
 * Pure, so the unit suite pins the grouping and the text rendering without a DOM.
 * Only the fields this group owns appear in it — the timeline and the manual-repair
 * block live in their own sections of the card.
 */
export function profileFieldGroups(application: ApplicationDto): ProfileFieldGroup[] {
  const field = (key: keyof typeof PROFILE_TEXT): ProfileFieldValue => {
    const render = PROFILE_TEXT[key] as ((application: ApplicationDto) => string | null) | undefined;
    return {
      field: key,
      label: FIELD_LABEL[key] ?? key,
      source: FIELD_SOURCE[key] ?? 'EPGU',
      text: render ? render(application) : null,
    };
  };

  return [
    {
      key: 'statement',
      title: 'Заявление',
      fields: [
        field('submittedBy'),
        field('submittedAt'),
        { ...field('statusUpdatedAt'), label: 'Дата статуса' },
      ],
    },
    {
      key: 'student',
      title: 'Ученик',
      fields: [
        field('isSpoStudent'),
        field('gender'),
        field('classCourse'),
        field('specialty'),
        field('specialtyCode'),
      ],
    },
    {
      key: 'program',
      title: 'Программа',
      fields: [
        field('direction'),
        field('courseName'),
        field('format'),
        field('orgName'),
        field('regionConfirmed'),
      ],
    },
  ];
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.8.3, T.Z21) — the snapshot address row of the profile drawer
// ---------------------------------------------------------------------------

/** The drawer's label for the ЕВИ evidence field the venue matcher read. */
export const SNAPSHOT_ADDRESS_LABEL = 'Адрес (из выгрузки)';

/** The one-line reason the address is red: the row has no venue to match it against. */
export const SNAPSHOT_ADDRESS_UNLINKED_HINT = 'не сопоставлен с площадкой';

/** How the drawer paints the address — red is the whole point of the row. */
export type SnapshotAddressTone = 'unlinked' | 'linked';

export interface SnapshotAddressView {
  /** Verbatim «Адрес площадки», as the api sent it. */
  text: string;
  /** `'unlinked'` renders red; `'linked'` renders the muted evidence grey. */
  tone: SnapshotAddressTone;
  /** Present only while red — the sentence that says what red means. */
  hint: string | null;
}

/**
 * The «Адрес (из выгрузки)» row of the card, or `null` when the row is not shown.
 *
 * The owner watches the unlinked offline rows (the Пугачёва/Саратов case): a snapshot
 * address that matches NO площадка is the evidence a manager needs to see before an
 * alias links the row, so it goes out RED while `venueId` is null and quiet grey once
 * the application is linked. `null`/absent `address` hides the row entirely — an empty
 * red field would read as a rendering bug, and «no evidence» is its own state (an
 * FTP-only row, or a blank address cell in the ЕВИ sheet).
 *
 * Pure: the unit project pins the three states without a DOM.
 */
export function snapshotAddressView(
  application: Pick<ApplicationDetailDto, 'address' | 'venueId'>,
): SnapshotAddressView | null {
  if (application.address === null || application.address === undefined) return null;
  const unlinked = application.venueId === null;
  return {
    text: application.address,
    tone: unlinked ? 'unlinked' : 'linked',
    hint: unlinked ? SNAPSHOT_ADDRESS_UNLINKED_HINT : null,
  };
}

// ---------------------------------------------------------------------------
// T.F1 — the ЕВИ deadline (linkReceivedAt + 10 days)
// ---------------------------------------------------------------------------

/**
 * The owner-approved rule: an applicant has 10 days from the link to pass the ЕВИ.
 * HOISTED to `@cb-manager/shared` (v1.9.66, T.Z204) so the cell, the api column and the
 * «Не прошёл ЕВИ» status derive from ONE constant; re-exported here for the existing
 * importers (the shared import above is the same binding).
 */
export { EVI_DEADLINE_DAYS };

export interface EviDeadline {
  /** The absolute deadline, ISO (deterministic for the tests and the `title`). */
  at: string;
  /** `now > deadline` — the chip turns red and the card says «просрочено». */
  overdue: boolean;
  /** Whole days left (negative when overdue; the day of the deadline itself is 0). */
  daysLeft: number;
  /** Human text, e.g. «до 11.09.2026, 2 дн.» / «до 11.09.2026, просрочено». */
  label: string;
}

/**
 * The «Дедлайн ЕВИ» of an application, or `null` when there is nothing to count from.
 *
 * Only a LINK_RECEIVED row has a running deadline: once the ЕВИ is passed (EVI_PASSED)
 * or the verdict is in (REJECTED / AWAITING_INFO / NO_DATA) the clock is not the thing
 * a manager acts on, and without `linkReceivedAt` there is no start of the clock.
 * `now` is injectable so the unit tests can freeze time.
 */
export function eviDeadline(
  application: Pick<ApplicationDto, 'status' | 'linkReceivedAt'>,
  now: Date = new Date(),
): EviDeadline | null {
  if (application.status !== 'LINK_RECEIVED' || application.linkReceivedAt === null) return null;

  const received = new Date(application.linkReceivedAt);
  if (Number.isNaN(received.getTime())) return null;

  const dayMs = EVI_DEADLINE_DAYS * 24 * 60 * 60 * 1000;
  const at = new Date(received.getTime() + dayMs);
  const overdue = now.getTime() > at.getTime();
  // Whole days towards (positive) or past (negative) the deadline, truncated towards
  // zero: an hour past the deadline reads «0», not «-1 день» — and never `-0`, which
  // is a distinct value for `Object.is` and a wrong string for the UI.
  const rawDays = Math.trunc((at.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
  const daysLeft = rawDays === 0 ? 0 : rawDays;

  const date = `${String(at.getUTCDate()).padStart(2, '0')}.${String(at.getUTCMonth() + 1).padStart(2, '0')}.${at.getUTCFullYear()}`;
  const relative = overdue ? 'просрочено' : daysLeft === 0 ? 'сегодня' : `${daysLeft} дн.`;

  return { at: at.toISOString(), overdue, daysLeft, label: `${date} · ${relative}` };
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.15, T.Z86/T.Z88) — the two «дней» cells
// ---------------------------------------------------------------------------

/**
 * The register's text for «Дней в статусе» / «Дней с ЕВИ»: the api's whole-day count
 * as «N дн.», or the shared «—» when the count is `null` (no status start the journal
 * ever named / never passed the ЕВИ). The api OWNS the day math (one instant per
 * page); the table only formats — it never re-derives a day, or its own clock would
 * quietly disagree with the sort the api answered with.
 *
 * Pure (no `Date.now()` here on purpose): the render is a function of the row alone,
 * exactly like the rest of the cells.
 */
export function daysCellText(value: number | null): string {
  return value === null ? MISSING : `${value} дн.`;
}
